import type {
  ArchivedDataQueryResult,
  ArchivedDataQueryVariables,
  CargoCompartmentAllocationId,
  CommitCargoCompartmentAllocationInput,
  CreatedOrExisting,
  CreateScenarioMutationResult,
  CreateScenarioMutationVariables,
  DeleteScenarioMutationResult,
  DeleteScenarioMutationVariables,
  HandledByCoaId,
  HandledByCoaInput,
  HandledBySpotId,
  HandledBySpotInput,
  MakeOptional,
  OffHireId,
  OffHireInput,
  OffHirePatch,
  OpenPositionsFragment,
  Order,
  OrderId,
  OrganizationId,
  PaymentUnit,
  PortCallId,
  PortCallInput,
  PortCallPatch,
  PublishOrdersMutation,
  PublishOrdersMutationVariables,
  ScenarioByClientIdQueryResult,
  ScenarioByClientIdQueryVariables,
  ScenarioInput,
  Scenarios_ScenarioFragment,
  ScenariosListQueryResult,
  ScenariosListQueryVariables,
  ScenarioTimeline_CargoCompartmentAllocationsFragment,
  ScenarioTimeline_CoaAssignmentFragment,
  ScenarioTimeline_HandledBySpotFragment,
  ScenarioTimeline_OffHireFragment,
  ScenarioTimeline_OrderFragment,
  ScenarioTimeline_PortCallFragment,
  ScenarioTimeline_SingleEventFragment,
  ScheduledOrderFragment,
  ScheduledOrderId,
  ScheduledOrderInput,
  ScheduledOrderPatch,
  ShipId,
  ShipmentId,
  SingleEventId,
  SingleEventInput,
  SingleEventPatch,
  UpdateScenarioMutationResult,
  UpdateScenarioMutationVariables,
  UserId,
  VesselReservationFragment,
  VoyageCharterId,
} from '../graphql';
import {
  ArchivedDataDocument,
  CreateScenarioDocument,
  DeleteScenarioDocument,
  PublishOrdersDocument,
  ScenarioByClientIdDocument,
  ScenariosListDocument,
  UpdateScenarioDocument,
  useArchivedDataQuery,
  useScenariosQuery,
  useScenarioUpdatedSubscription,
} from '../graphql';
import type { MutableRefObject } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import type { Patch } from 'immer';
import { applyPatches, castDraft, produceWithPatches } from 'immer';
import AppStateContext from './contexts/AppStateContext';

import type {
  CargoAllocation,
  CargoEntry,
  CoaEntry,
  Entry,
  OffHireEntry,
  PortCallEntry,
  SingleEntry,
  SpotEntry,
  TCOrderEntry,
} from '@seaber/common/core/entries';
import {
  EventCategory,
  isCoaEntry,
  isOffHireEntry,
  isPortCallEntry,
  isSingleEntry,
  isSpotEntry,
  isTCOrderEntry,
  minimumEventDuration,
} from '@seaber/common/core/entries';
import type { ChangedEntries, WithDbId } from './core/Core';
import {
  checkFixEntryTimestamps,
  parseBunkerOverrides,
  parseBunkerPriceSource,
  workingHoursSchema,
} from './core/Core';
import { calculateMassAndVolumeRanges } from './core/Capacity';
import type { Events } from './core/TimeData';
import { CoreEventType, isCargoEntry } from './core/TimeData';
import { clearTimeDataCache } from './core/TimeDataCache';
import { MultiKey, type Narrowable } from './core/MultiKey';
import type { Id as ToastNotificationId } from 'react-toastify';
import { toast } from 'react-toastify';
import cloneDeep from 'lodash/cloneDeep';
import {
  dateStringToNum,
  dateStringToNumOpt,
  numToDateString,
  numToDateStringOpt,
  numToStringOpt,
  stringToNumOpt,
} from './core/util';
import {
  memoize,
  memoize2,
  memoize3,
  memoize4,
  memoize5,
} from '@seaber/common/lib/memoize';
import type { ExactProps, NoExtraProps } from '../lib/util';
import {
  assertDefined,
  assertedDefined,
  exactProps,
  exhaustiveCheck,
  get,
  isDefined,
  mapKeyBy,
  parseWithMaps,
  replaceContents,
  serializeWithMaps,
} from '../lib/util';
import { readStore, writeStore } from '../lib/localStore';
import type {
  CommittedPlanningData,
  GetScenarioData,
  PersistedShipment,
  ScenarioOrder,
} from '../lib/planningData';
import {
  capitalize,
  compact,
  difference,
  findLast,
  omit,
  orderBy,
  pick,
  sortBy,
  sumBy,
  union,
  uniq,
} from 'lodash';
import type { EventTimeline } from './core/Timelines';
import type { ClientId } from './core/ClientId';
import {
  generateClientId,
  parseClientId,
  parseClientIdOpt,
} from './core/ClientId';
import { useOnce } from './hooks/misc';
import { useLocalSetting } from './hooks/useLocalConfig';
import {
  doCommitScenario,
  pruneUnusedPortCalls,
  isAutoSyncEnabledForScenario,
  parseArchivedData,
  updateScenarioFromArchivedState,
  updateScenarioFromCommitResult,
  updateScenarioFromTimelines,
} from './ScenarioUtil';
import moment from 'moment';
import {
  LATEST_MIGRATION_VERSION,
  migrateAll,
} from './scenario/migrations/migrate';
import { useCurrentUser } from '../state/zustand';
import { useAtom, useAtomValue } from 'jotai';
import useEvent from './hooks/useEvent';
import { atom } from 'jotai/vanilla';
import { atomFamily } from 'jotai/utils';
import * as zod from 'zod';
import { persistedAtom } from '../state/utils/persistedAtom';
import { useNotify } from './notifications/ToastNotifications';
import { useVoyageNamingRules } from './hooks/useVoyageNamingRules';
import type { MergeDirectives } from './MergeScenarios';
import {
  getMergedScenario,
  MERGE_SOURCE_IS_COMMIT,
  MERGE_SOURCE_NOT_COMMIT,
} from './MergeScenarios';
import type {
  BackupSchedule,
  EstimateOpenPosition,
  EstimateTimeline,
  EstimateVesselReservation,
  InitialSchedule,
  InternalBackupSchedule,
  InternalInitialSchedule,
  InternalMasterSchedule,
  InternalSuggestionSchedule,
  InternalUserSchedule,
  MasterSchedule,
  PatchShipment,
  ScenarioOrderPatch,
  ScenarioSchedule,
  ScenarioShipment,
  ScenarioShipmentExtraFreight,
  SuggestionSchedule,
  UserSchedule,
} from '@seaber/common/core/scenario';
import {
  type DeleteEventAction,
  EntryState,
  type FreightRateOverridesByOrderId,
  getOrderAssignments,
  SCENARIO_DATA_SCHEMA_VERSION,
  type ShipmentFreightOverridesByShipmentId,
} from '@seaber/common/core/scenario';
import { DefaultMap, mapGroupBy } from '@seaber/common/lib/collections';
import equal from '@wry/equality';
import applyFreightRateOverridesToVC from './timeline/estimator/freightRate/applyFreightRateOverridesToVC';
import applyEstimateReservationsAndOpenPositions from './timeline/estimator/applyEstimateReservationsAndOpenPositions';
import {
  toEstimateOpenPosition,
  toEstimateVesselReservation,
} from './timeline/estimator/estimateFragments';

export * from '@seaber/common/core/scenario';

export const apolloMutationCompletedAtAtom = atom<number>(0);

export type TCScheduleEntry = TCOrderEntry | SingleEntry | OffHireEntry;

export type ScheduleEntry = TCScheduleEntry | CoaEntry | SpotEntry;

export const scheduledOrderFragmentToTCOrderEntry = memoize3(
  (
    fragment: ScheduledOrderFragment,
    ordersById: Map<number, ScenarioTimeline_OrderFragment>,
    shipmentByDbId: Map<number, PersistedShipment>,
  ): WithDbId<Required<TCOrderEntry>, ScheduledOrderId> => {
    const {
      id,
      loadingStart,
      loadingDuration,
      dischargingDuration,
      loadingDurationOverride,
      loadingStartOverride,
      loadingSeaDurationOverride,
      loadingEnd,
      dischargingStart,
      dischargingDurationOverride,
      dischargingStartOverride,
      dischargingSeaDurationOverride,
      dischargingEnd,
      updatedAt,
      clientId,
      vesselId,
      orderId,
      shared,
      loadingConcurrent,
      dischargingConcurrent,
      cleaningOmitted,
    } = fragment;

    const order = ordersById.get(fragment.orderId);
    const pcRelations =
      order?.orderPortCallRelationsByOrderId.nodes.filter(isDefined) ?? [];

    const shipment = isDefined(order?.shipmentId)
      ? shipmentByDbId.get(order.shipmentId)
      : null;

    // noinspection UnnecessaryLocalVariableJS
    const result: ExactProps<
      WithDbId<TCOrderEntry, ScheduledOrderId>,
      typeof fragment
    > = {
      dbId: id,
      clientId: parseClientId(clientId),
      category: EventCategory.TC,
      vesselId: vesselId ?? null,
      loadingDuration: stringToNumOpt(loadingDuration) ?? 0,
      loadingDurationOverride: stringToNumOpt(loadingDurationOverride) ?? null,
      dischargingDuration: stringToNumOpt(dischargingDuration) ?? 0,
      dischargingDurationOverride:
        stringToNumOpt(dischargingDurationOverride) ?? null,
      loadingSeaDurationOverride:
        stringToNumOpt(loadingSeaDurationOverride) ?? null,
      dischargingSeaDurationOverride:
        stringToNumOpt(dischargingSeaDurationOverride) ?? null,
      loadingStart: dateStringToNum(loadingStart),
      loadingStartOverride: dateStringToNumOpt(loadingStartOverride),
      loadingEnd: dateStringToNum(loadingEnd),
      dischargingStart: dateStringToNum(dischargingStart),
      dischargingStartOverride: dateStringToNumOpt(dischargingStartOverride),
      dischargingEnd: dateStringToNum(dischargingEnd),
      loadingPortCallId: parseClientIdOpt(
        pcRelations.find((rel) => rel.isLoadPort)?.portCallByPortCallId
          ?.clientId ?? null,
      ),
      dischargingPortCallId: parseClientIdOpt(
        pcRelations.find((rel) => !rel.isLoadPort)?.portCallByPortCallId
          ?.clientId ?? null,
      ),
      updatedAt: dateStringToNumOpt(updatedAt),
      archived: order?.archived,
      orderId,
      shared,
      loadingConcurrent,
      dischargingConcurrent,
      shipmentId: shipment?.clientId ?? null,
      cleaningOmitted,
    };

    return result;
  },
);

export const singleEventFragmentToSingleEntry = memoize2(
  (
    fragment: ScenarioTimeline_SingleEventFragment,
    portCalls: Map<number, ScenarioTimeline_PortCallFragment>,
  ): WithDbId<Required<SingleEntry>, SingleEventId> => {
    const {
      id,
      clientId,
      vesselId,
      meta,
      start,
      startOverride,
      duration,
      seaDurationOverride,
      end,
      updatedAt,
      archived,
      shared,
      type,
      locationId,
      locked,
      concurrent,
      cleaningAssortmentExclusionId,
    } = fragment;

    const pcRel =
      get(
        compact(fragment.singleEventPortCallRelationsBySingleEventId.nodes),
        0,
      ) ?? null;
    const portCallId: ClientId | null =
      pcRel &&
      parseClientIdOpt(portCalls.get(pcRel.portCallId)?.clientId ?? null);

    // noinspection UnnecessaryLocalVariableJS
    const result: ExactProps<
      WithDbId<SingleEntry, SingleEventId>,
      typeof fragment
    > = {
      dbId: id,
      clientId: parseClientId(clientId),
      category: EventCategory.Single,
      vesselId: vesselId ?? null,
      meta: meta ?? null,
      start: dateStringToNum(start),
      startOverride: dateStringToNumOpt(startOverride),
      duration: +duration,
      end: dateStringToNum(end),
      seaDurationOverride: stringToNumOpt(seaDurationOverride) ?? null,
      portCallId,
      updatedAt: dateStringToNumOpt(updatedAt),
      archived,
      shared,
      type,
      locationId,
      locked,
      concurrent,
      cleaningAssortmentExclusionId: cleaningAssortmentExclusionId ?? null,
    };

    return result;
  },
);

export const offHireFragmentToOffHireEntry = memoize(
  (
    fragment: ScenarioTimeline_OffHireFragment,
  ): WithDbId<Required<OffHireEntry>, OffHireId> => {
    const {
      id,
      clientId,
      vesselId,
      meta,
      type,
      start,
      end,
      updatedAt,
      duration,
      shared,
      archived,
      locked,
      userId,
      planningOrganisationId,
      locationBId,
      locationAId,
      tcOutRate,
      commissionPct,
      ballastBonus,
      ballastBonusCommissionable,
    } = fragment;

    // noinspection UnnecessaryLocalVariableJS
    const result: ExactProps<
      WithDbId<OffHireEntry, OffHireId>,
      typeof fragment
    > = {
      dbId: id,
      clientId: parseClientId(clientId),
      category: EventCategory.OffHire,
      vesselId: vesselId ?? null,
      meta: meta ?? null,
      type: type ?? null,
      start: dateStringToNum(start),
      end: dateStringToNum(end),
      duration: +duration,
      updatedAt: dateStringToNumOpt(updatedAt),
      shared,
      archived,
      locked,
      userId,
      planningOrganisationId,
      locationBId: locationBId,
      locationAId: locationAId,
      tcOutRate: tcOutRate ?? null,
      commissionPct: commissionPct ?? null,
      ballastBonus: ballastBonus ?? null,
      ballastBonusCommissionable: ballastBonusCommissionable ?? false,
    };

    return result;
  },
);

const endFromStartAndDuration = <T extends string | number | null | undefined>(
  start: T,
  duration: number | undefined,
): T => {
  if (start === undefined || start === null) {
    return start;
  } else if (typeof duration !== 'number') {
    return start;
  } else if (typeof start === 'number') {
    return (start + duration) as T;
  } else if (typeof start === 'string') {
    return numToDateString(dateStringToNum(start) + duration) as T;
  } else {
    throw new Error(`Unexpected type of start timestamp`);
  }
};

export const spotEntryToSpotInput = (entry: SpotEntry): HandledBySpotInput => {
  const {
    loadingStart,
    loadingDuration,
    loadingDurationOverride,
    loadingStartOverride,
    loadingEnd,
    loadingSeaDurationOverride,
    dischargingStart,
    dischargingDuration,
    dischargingDurationOverride,
    dischargingStartOverride,
    dischargingEnd,
    dischargingSeaDurationOverride,
    updatedAt,
    orderId,
    shared,
    dischargingConcurrent,
    metadata,
    loadingConcurrent,
    vesselId,
    ownerOrganisationId,
    clientId,
  } = entry;

  // noinspection UnnecessaryLocalVariableJS
  const result: ExactProps<HandledBySpotInput, typeof entry> = {
    loadingStart: numToDateString(loadingStart),
    loadingDuration: loadingDuration.toString(),
    loadingDurationOverride: numToStringOpt(loadingDurationOverride) ?? null,
    loadingStartOverride: numToDateStringOpt(loadingStartOverride),
    loadingEnd: numToDateString(loadingEnd),
    loadingSeaDurationOverride:
      numToStringOpt(loadingSeaDurationOverride) ?? null,
    dischargingStart: numToDateString(dischargingStart),
    dischargingDuration: dischargingDuration.toString(),
    dischargingDurationOverride:
      numToStringOpt(dischargingDurationOverride) ?? null,
    dischargingStartOverride: numToDateStringOpt(dischargingStartOverride),
    dischargingEnd: numToDateString(dischargingEnd),
    dischargingSeaDurationOverride:
      numToStringOpt(dischargingSeaDurationOverride) ?? null,
    updatedAt: numToDateStringOpt(updatedAt),
    archived: false,
    orderId,
    shared,
    dischargingConcurrent,
    metadata,
    loadingConcurrent,
    vesselId,
    ownerOrganisationId,
    clientId,
  };

  return result;
};

export const coaEntryToCoaInput = (entry: CoaEntry): HandledByCoaInput => {
  const {
    loadingStart,
    loadingStartOverride,
    loadingEnd,
    loadingDuration,
    loadingDurationOverride,
    loadingSeaDurationOverride,
    dischargingStart,
    dischargingStartOverride,
    dischargingEnd,
    dischargingDuration,
    dischargingDurationOverride,
    dischargingSeaDurationOverride,
    updatedAt,
    shared,
    orderId,
    coaId,
    vesselId,
    metadata,
    loadingConcurrent,
    dischargingConcurrent,
    clientId,
  } = entry;

  // noinspection UnnecessaryLocalVariableJS
  const result: ExactProps<HandledByCoaInput, typeof entry> = {
    loadingStart: numToDateString(loadingStart),
    loadingDuration: loadingDuration.toString(),
    loadingDurationOverride: numToStringOpt(loadingDurationOverride) ?? null,
    loadingStartOverride: numToDateStringOpt(loadingStartOverride),
    loadingEnd: numToDateString(loadingEnd),
    loadingSeaDurationOverride:
      numToStringOpt(loadingSeaDurationOverride) ?? null,
    dischargingStart: numToDateString(dischargingStart),
    dischargingStartOverride: numToDateStringOpt(dischargingStartOverride),
    dischargingEnd: numToDateString(dischargingEnd),
    dischargingDuration: dischargingDuration.toString(),
    dischargingDurationOverride:
      numToStringOpt(dischargingDurationOverride) ?? null,
    dischargingSeaDurationOverride:
      numToStringOpt(dischargingSeaDurationOverride) ?? null,
    updatedAt: numToDateStringOpt(updatedAt),
    archived: false,
    shared: shared ?? false,
    orderId,
    coaId,
    vesselId,
    metadata,
    loadingConcurrent,
    dischargingConcurrent,
    clientId,
  };

  return result;
};

export const tcOrderEntryToScheduledOrderInput = (
  entry: TCOrderEntry &
    Pick<ScheduledOrderInput, 'planningOrganisationId' | 'userId' | 'locked'>,
): ScheduledOrderInput => {
  const {
    loadingStart,
    loadingStartOverride,
    loadingEnd,
    loadingSeaDurationOverride,
    dischargingStart,
    dischargingStartOverride,
    dischargingEnd,
    dischargingSeaDurationOverride,
    updatedAt,
    vesselId,
    orderId,
    shared,
    dischargingConcurrent,
    clientId,
    loadingConcurrent,
    userId,
    planningOrganisationId,
    locked,
    dischargingDurationOverride,
    loadingDurationOverride,
    loadingDuration,
    dischargingDuration,
    cleaningOmitted,
  } = entry;

  // noinspection UnnecessaryLocalVariableJS
  const result: NoExtraProps<Required<ScheduledOrderInput>, typeof entry> = {
    loadingStart: numToDateString(loadingStart),
    loadingStartOverride: numToDateStringOpt(loadingStartOverride),
    loadingEnd: numToDateString(loadingEnd),
    loadingSeaDurationOverride:
      numToStringOpt(loadingSeaDurationOverride) ?? null,
    dischargingStart: numToDateString(dischargingStart),
    dischargingStartOverride: numToDateStringOpt(dischargingStartOverride),
    dischargingEnd: numToDateString(dischargingEnd),
    dischargingSeaDurationOverride:
      numToStringOpt(dischargingSeaDurationOverride) ?? null,
    updatedAt: numToDateStringOpt(updatedAt),
    amount: null,
    meta: null,
    createdAt: numToDateString(updatedAt ?? Date.now()),
    vesselId: vesselId ?? null,
    archived: false,
    orderId,
    shared,
    dischargingConcurrent,
    clientId,
    loadingConcurrent,
    userId,
    planningOrganisationId,
    locked,
    dischargingDurationOverride,
    loadingDurationOverride,
    loadingDuration,
    dischargingDuration,
    cleaningOmitted: cleaningOmitted ?? false,
  };

  return result;
};

export const tcOrderEntryToScheduledOrderPatch = (
  entry: TCOrderEntry,
): ScheduledOrderPatch => {
  const {
    loadingStart,
    loadingStartOverride,
    loadingEnd,
    loadingSeaDurationOverride,
    dischargingStart,
    dischargingStartOverride,
    dischargingEnd,
    dischargingSeaDurationOverride,
    updatedAt,
    orderId,
    shared,
    dischargingConcurrent,
    clientId,
    loadingDurationOverride,
    dischargingDurationOverride,
    loadingConcurrent,
    dischargingDuration,
    vesselId,
    loadingDuration,
    cleaningOmitted,
  } = entry;

  // noinspection UnnecessaryLocalVariableJS
  const result: NoExtraProps<ScheduledOrderPatch, typeof entry> = {
    loadingStart: numToDateString(loadingStart),
    loadingStartOverride: numToDateStringOpt(loadingStartOverride),
    loadingEnd: numToDateString(loadingEnd),
    loadingSeaDurationOverride: numToStringOpt(loadingSeaDurationOverride),
    dischargingStart: numToDateString(dischargingStart),
    dischargingStartOverride: numToDateStringOpt(dischargingStartOverride),
    dischargingEnd: numToDateString(dischargingEnd),
    dischargingSeaDurationOverride: numToStringOpt(
      dischargingSeaDurationOverride,
    ),
    updatedAt: numToDateStringOpt(updatedAt),
    orderId,
    shared,
    dischargingConcurrent,
    clientId,
    loadingDurationOverride,
    dischargingDurationOverride,
    loadingConcurrent,
    dischargingDuration,
    vesselId,
    loadingDuration,
    cleaningOmitted,
  };

  return result;
};

export const singleEntryToInput = (
  entry: SingleEntry &
    Pick<SingleEventInput, 'planningOrganisationId' | 'userId'>,
): SingleEventInput => {
  const {
    start,
    startOverride,
    duration,
    updatedAt,
    clientId,
    userId,
    planningOrganisationId,
    locked,
    type,
    locationId,
    vesselId,
    meta,
    seaDurationOverride,
    concurrent,
    cleaningAssortmentExclusionId,
  } = entry;
  // noinspection UnnecessaryLocalVariableJS
  const input: NoExtraProps<SingleEventInput, typeof entry> = {
    start: numToDateString(start),
    startOverride: numToDateStringOpt(startOverride),
    duration: duration.toString(),
    end: numToDateString(endFromStartAndDuration(start, duration)),
    shared: false,
    updatedAt: numToDateStringOpt(updatedAt),
    clientId,
    userId,
    planningOrganisationId,
    locked,
    type,
    locationId,
    vesselId,
    meta,
    seaDurationOverride,
    concurrent,
    cleaningAssortmentExclusionId,
  };
  return input;
};

export const singleEntryToPatch = (entry: SingleEntry): SingleEventPatch => {
  const {
    start,
    startOverride,
    duration,
    updatedAt,
    clientId,
    shared,
    type,
    locationId,
    locked,
    vesselId,
    meta,
    seaDurationOverride,
    concurrent,
  } = entry;
  // noinspection UnnecessaryLocalVariableJS
  const input: NoExtraProps<SingleEventPatch, typeof entry> = {
    duration: duration.toString(),
    start: numToDateString(start),
    end: numToDateString(endFromStartAndDuration(start, duration)),
    startOverride: numToDateStringOpt(startOverride),
    updatedAt: numToDateStringOpt(updatedAt),
    clientId,
    shared,
    type,
    locationId,
    locked,
    vesselId,
    meta,
    seaDurationOverride,
    concurrent,
  };
  return input;
};

export const portCallEntryToInput = (entry: PortCallEntry): PortCallInput => {
  let { plannedBerthing, plannedDeparture } = entry;
  const {
    plannedArrival,
    plannedArrivalOverride,
    plannedArrivalOverrideSourcedAt,
    plannedBerthingOverride,
    plannedBerthingOverrideSourcedAt,
    plannedDepartureOverride,
    plannedDepartureOverrideSourcedAt,
    updatedAt,
    legAvoidsChannels,
    seaDurationOverride,
    oneTimeOverrideCost,
    portCostDefaultId,
    useHistoricalPortCost,
    useLatestHistoricalPortCost,
    useOneTimeOverrideCost,
    useIntegrationPortCost,
    totalLoadDurationOverride,
    totalDischargeDurationOverride,
    channelDurationOverhead,
    clientId,
    vesselId,
    locationId,
    previousLocationId,
    oneTimeOverrideChannelCost,
    plannedArrivalOverrideSourcedFrom,
    plannedArrivalDuration,
    plannedBerthingOverrideSourcedFrom,
    plannedBerthingDuration,
    plannedDepartureOverrideSourcedFrom,
    plannedDepartureDuration,
    channelCostDefaultId,
    bunkerOverrides,
    bunkerPriceSource,
    weatherFactor,
  } = entry;

  plannedBerthing = Math.max(plannedArrival + 1, plannedBerthing);
  plannedDeparture = Math.max(plannedBerthing + 1, plannedDeparture);

  // noinspection UnnecessaryLocalVariableJS
  const input: NoExtraProps<PortCallInput, typeof entry> = {
    plannedArrival: numToDateString(plannedArrival),
    plannedBerthing: numToDateString(plannedBerthing),
    plannedDeparture: numToDateString(plannedDeparture),
    plannedArrivalOverride: numToDateStringOpt(plannedArrivalOverride),
    plannedArrivalOverrideSourcedAt: numToDateStringOpt(
      plannedArrivalOverrideSourcedAt,
    ),
    plannedBerthingOverride: numToDateStringOpt(plannedBerthingOverride),
    plannedBerthingOverrideSourcedAt: numToDateStringOpt(
      plannedBerthingOverrideSourcedAt,
    ),
    plannedDepartureOverride: numToDateStringOpt(plannedDepartureOverride),
    plannedDepartureOverrideSourcedAt: numToDateStringOpt(
      plannedDepartureOverrideSourcedAt,
    ),
    seaDurationOverride: numToStringOpt(seaDurationOverride),
    legAvoidsChannels: legAvoidsChannels ?? false,
    oneTimeOverrideCost: numToStringOpt(oneTimeOverrideCost) ?? null,
    portCostDefaultId: portCostDefaultId,
    useHistoricalPortCost: useHistoricalPortCost ?? false,
    useLatestHistoricalPortCost: useLatestHistoricalPortCost ?? false,
    useOneTimeOverrideCost: useOneTimeOverrideCost ?? false,
    useIntegrationPortCost: useIntegrationPortCost ?? false,
    channelDurationOverhead: numToStringOpt(channelDurationOverhead),
    updatedAt: numToDateStringOpt(updatedAt),
    totalDischargeDurationOverride:
      numToStringOpt(totalDischargeDurationOverride) ?? null,
    totalLoadDurationOverride:
      numToStringOpt(totalLoadDurationOverride) ?? null,
    clientId,
    vesselId,
    locationId,
    previousLocationId,
    oneTimeOverrideChannelCost,
    plannedArrivalOverrideSourcedFrom,
    plannedArrivalDuration,
    plannedBerthingOverrideSourcedFrom,
    plannedBerthingDuration,
    plannedDepartureOverrideSourcedFrom,
    plannedDepartureDuration,
    channelCostDefaultId,
    bunkerOverrides: bunkerOverrides ? JSON.stringify(bunkerOverrides) : null,
    bunkerPriceSource: bunkerPriceSource
      ? JSON.stringify(bunkerPriceSource)
      : null,
    weatherFactor,
  };
  return input;
};

export const portCallEntryToPatch = (entry: PortCallEntry): PortCallPatch => {
  return portCallEntryToInput(entry);
};

export const cargoAllocationEntryToInput = (
  entry: CargoAllocation,
  createdOrExistingItems: Map<ClientId, CreatedOrExisting>,
): CommitCargoCompartmentAllocationInput | null => {
  const { amount, clientId, compartmentId, orderId, scheduledOrderClientId } =
    entry;

  const scheduledOrderId = createdOrExistingItems.get(scheduledOrderClientId);
  if (!scheduledOrderId) {
    console.warn(`Expected scheduledOrder to be created before allocation`);
    return null;
  }

  // noinspection UnnecessaryLocalVariableJS
  const input: NoExtraProps<
    CommitCargoCompartmentAllocationInput,
    typeof entry
  > = {
    amount,
    clientId,
    compartmentId,
    orderId,
    scheduledOrderId,
    scheduledOrderClientId,
  };
  return input;
};

export const offHireEntryToInput = (entry: OffHireEntry): OffHireInput => {
  const {
    start,
    end,
    updatedAt,
    duration,
    clientId,
    vesselId,
    locked,
    type,
    userId,
    planningOrganisationId,
    locationBId,
    locationAId,
    meta,
    tcOutRate,
    commissionPct,
    ballastBonus,
    ballastBonusCommissionable,
  } = entry;
  // noinspection UnnecessaryLocalVariableJS
  const input: NoExtraProps<OffHireInput, typeof entry> = {
    duration: duration.toString(),
    start: numToDateString(start),
    end: numToDateString(end),
    updatedAt: numToDateStringOpt(updatedAt),
    shared: false,
    clientId,
    vesselId,
    locked,
    type,
    userId,
    planningOrganisationId,
    locationBId,
    locationAId,
    meta,
    tcOutRate,
    commissionPct,
    ballastBonus,
    ballastBonusCommissionable,
  };
  return input;
};

export const offHireEntryToPatch = (entry: OffHireEntry): OffHirePatch => {
  const {
    start,
    end,
    updatedAt,
    duration,
    clientId,
    vesselId,
    locked,
    type,
    planningOrganisationId,
    locationBId,
    locationAId,
    meta,
    shared,
    userId,
    tcOutRate,
    commissionPct,
    ballastBonus,
    ballastBonusCommissionable,
  } = entry;
  // noinspection UnnecessaryLocalVariableJS
  const input: NoExtraProps<OffHirePatch, typeof entry> = {
    duration: duration.toString(),
    start: numToDateString(start),
    end: numToDateString(end),
    updatedAt: numToDateStringOpt(updatedAt),
    clientId,
    vesselId,
    locked,
    type,
    planningOrganisationId,
    locationBId,
    locationAId,
    meta,
    shared,
    userId,
    tcOutRate,
    commissionPct,
    ballastBonus,
    ballastBonusCommissionable,
  };
  return input;
};

export const coaFragmentToEntry = memoize2(
  (
    fragment: ScenarioTimeline_CoaAssignmentFragment,
    ordersById: Map<number, ScenarioTimeline_OrderFragment>,
  ): WithDbId<Required<CoaEntry>, HandledByCoaId> => {
    const {
      id,
      loadingStart,
      loadingDuration,
      loadingDurationOverride,
      loadingStartOverride,
      loadingSeaDurationOverride,
      loadingEnd,
      dischargingStart,
      dischargingDuration,
      dischargingDurationOverride,
      dischargingStartOverride,
      dischargingSeaDurationOverride,
      dischargingEnd,
      updatedAt,
      clientId,
      vesselId,
      metadata,
      loadingConcurrent,
      dischargingConcurrent,
      coaId,
      orderId,
      shared,
    } = fragment;

    const order = ordersById.get(fragment.orderId);
    const pcRelations =
      order?.orderPortCallRelationsByOrderId.nodes.filter(isDefined) ?? [];

    // noinspection UnnecessaryLocalVariableJS
    const result: ExactProps<
      WithDbId<CoaEntry, HandledByCoaId>,
      typeof fragment
    > = {
      vesselId: vesselId ?? null,
      metadata: metadata ?? null,
      dbId: id,
      clientId: parseClientId(clientId),
      category: EventCategory.COA,
      loadingStart: dateStringToNum(loadingStart),
      loadingDuration: stringToNumOpt(loadingDuration) ?? 0,
      loadingDurationOverride: stringToNumOpt(loadingDurationOverride) ?? null,
      loadingStartOverride: dateStringToNumOpt(loadingStartOverride),
      loadingEnd: dateStringToNum(loadingEnd),
      dischargingStart: dateStringToNum(dischargingStart),
      dischargingDuration: stringToNumOpt(dischargingDuration) ?? 0,
      dischargingDurationOverride:
        stringToNumOpt(dischargingDurationOverride) ?? null,
      dischargingStartOverride: dateStringToNumOpt(dischargingStartOverride),
      dischargingEnd: dateStringToNum(dischargingEnd),
      loadingSeaDurationOverride:
        stringToNumOpt(loadingSeaDurationOverride) ?? null,
      dischargingSeaDurationOverride:
        stringToNumOpt(dischargingSeaDurationOverride) ?? null,
      loadingPortCallId: parseClientIdOpt(
        pcRelations.find((rel) => rel.isLoadPort)?.portCallByPortCallId
          ?.clientId ?? null,
      ),
      dischargingPortCallId: parseClientIdOpt(
        pcRelations.find((rel) => !rel.isLoadPort)?.portCallByPortCallId
          ?.clientId ?? null,
      ),
      updatedAt: dateStringToNumOpt(updatedAt),
      archived: order?.archived ?? false,
      loadingConcurrent,
      dischargingConcurrent,
      coaId,
      orderId,
      shared,
    };

    return result;
  },
);

export const spotFragmentToEntry = memoize2(
  (
    fragment: ScenarioTimeline_HandledBySpotFragment,
    ordersById: Map<number, ScenarioTimeline_OrderFragment>,
  ): WithDbId<Required<SpotEntry>, HandledBySpotId> => {
    const {
      id,
      orderId,
      loadingStart,
      loadingDuration,
      loadingDurationOverride,
      loadingStartOverride,
      loadingSeaDurationOverride,
      loadingEnd,
      dischargingStart,
      dischargingDuration,
      dischargingDurationOverride,
      dischargingStartOverride,
      dischargingSeaDurationOverride,
      dischargingEnd,
      updatedAt,
      clientId,
      vesselId,
      metadata,
      ownerOrganisationId,
      loadingConcurrent,
      dischargingConcurrent,
      shared,
    } = fragment;

    assertDefined(orderId, `Expected HandledBySpot to have orderId`);

    const order = ordersById.get(orderId);
    const pcRelations =
      order?.orderPortCallRelationsByOrderId.nodes.filter(isDefined) ?? [];

    // noinspection UnnecessaryLocalVariableJS
    const result: ExactProps<
      WithDbId<SpotEntry, HandledBySpotId>,
      typeof fragment
    > = {
      vesselId: vesselId ?? null,
      metadata: metadata ?? null,
      dbId: id,
      clientId: parseClientId(clientId),
      category: EventCategory.Spot,
      orderId,
      ownerOrganisationId: ownerOrganisationId ?? null,
      loadingStart: dateStringToNum(loadingStart),
      loadingDuration: stringToNumOpt(loadingDuration) ?? 0,
      loadingDurationOverride: stringToNumOpt(loadingDurationOverride) ?? null,
      loadingStartOverride: dateStringToNumOpt(loadingStartOverride),
      loadingEnd: dateStringToNum(loadingEnd),
      dischargingStart: dateStringToNum(dischargingStart),
      dischargingDuration: stringToNumOpt(dischargingDuration) ?? 0,
      dischargingDurationOverride:
        stringToNumOpt(dischargingDurationOverride) ?? null,
      dischargingStartOverride: dateStringToNumOpt(dischargingStartOverride),
      dischargingEnd: dateStringToNum(dischargingEnd),
      loadingSeaDurationOverride:
        stringToNumOpt(loadingSeaDurationOverride) ?? null,
      dischargingSeaDurationOverride:
        stringToNumOpt(dischargingSeaDurationOverride) ?? null,
      loadingPortCallId: parseClientIdOpt(
        pcRelations.find((rel) => rel.isLoadPort)?.portCallByPortCallId
          ?.clientId ?? null,
      ),
      dischargingPortCallId: parseClientIdOpt(
        pcRelations.find((rel) => !rel.isLoadPort)?.portCallByPortCallId
          ?.clientId ?? null,
      ),
      updatedAt: dateStringToNumOpt(updatedAt),
      archived: order?.archived,
      loadingConcurrent,
      dischargingConcurrent,
      shared,
    };

    return result;
  },
);

export const portCallFragmentToEntry = memoize(
  (
    fragment: ScenarioTimeline_PortCallFragment,
  ): WithDbId<Required<PortCallEntry>, PortCallId> => {
    let plannedArrival = dateStringToNumOpt(fragment.plannedArrival);
    let plannedBerthing = dateStringToNumOpt(fragment.plannedBerthing);
    let plannedDeparture = dateStringToNumOpt(fragment.plannedDeparture);

    plannedArrival =
      plannedArrival ??
      (plannedBerthing && plannedBerthing - 1) ??
      (plannedDeparture && plannedDeparture - 2) ??
      Date.now();
    plannedBerthing = Math.max(plannedArrival + 1, plannedBerthing ?? 0);
    plannedDeparture = Math.max(plannedBerthing + 1, plannedDeparture ?? 0);

    return {
      dbId: fragment.id,
      category: EventCategory.PortCall,
      clientId: parseClientId(fragment.clientId),
      plannedArrival,
      plannedBerthing,
      plannedDeparture,
      plannedArrivalOverride: dateStringToNumOpt(
        fragment.plannedArrivalOverride,
      ),
      plannedArrivalOverrideSourcedAt: dateStringToNumOpt(
        fragment.plannedArrivalOverrideSourcedAt,
      ),
      plannedArrivalOverrideSourcedFrom:
        fragment.plannedArrivalOverrideSourcedFrom ?? null,
      plannedBerthingOverride: dateStringToNumOpt(
        fragment.plannedBerthingOverride,
      ),
      plannedBerthingOverrideSourcedAt: dateStringToNumOpt(
        fragment.plannedBerthingOverrideSourcedAt,
      ),
      plannedBerthingOverrideSourcedFrom:
        fragment.plannedBerthingOverrideSourcedFrom ?? null,
      plannedDepartureOverride: dateStringToNumOpt(
        fragment.plannedDepartureOverride,
      ),
      plannedDepartureOverrideSourcedAt: dateStringToNumOpt(
        fragment.plannedDepartureOverrideSourcedAt,
      ),
      plannedDepartureOverrideSourcedFrom:
        fragment.plannedDepartureOverrideSourcedFrom ?? null,
      plannedArrivalDuration:
        stringToNumOpt(fragment.plannedArrivalDuration) ?? null,
      plannedBerthingDuration:
        stringToNumOpt(fragment.plannedBerthingDuration) ?? null,
      plannedDepartureDuration:
        stringToNumOpt(fragment.plannedDepartureDuration) ?? null,
      seaDurationOverride: fragment.plannedArrivalOverrideSourcedFrom
        ? null // Sourced start time takes precedence over manual sea duration override
        : (stringToNumOpt(fragment.seaDurationOverride) ?? null),
      legAvoidsChannels: fragment.legAvoidsChannels ?? false,
      totalLoadDurationOverride:
        stringToNumOpt(fragment.totalLoadDurationOverride) ?? null,
      totalDischargeDurationOverride:
        stringToNumOpt(fragment.totalDischargeDurationOverride) ?? null,
      locationId: fragment.locationId,
      previousLocationId: fragment.previousLocationId ?? null,
      vesselId: fragment.vesselId ?? null,
      updatedAt: dateStringToNumOpt(fragment.updatedAt),
      archived: fragment.archived,
      channelCostDefaultId: fragment.channelCostDefaultId ?? null,
      oneTimeOverrideChannelCost:
        stringToNumOpt(fragment.oneTimeOverrideChannelCost) ?? null,
      oneTimeOverrideCost: stringToNumOpt(fragment.oneTimeOverrideCost) ?? null,
      portCostDefaultId: fragment.portCostDefaultId ?? null,
      useHistoricalPortCost: fragment.useHistoricalPortCost ?? false,
      useLatestHistoricalPortCost:
        fragment.useLatestHistoricalPortCost ?? false,
      useOneTimeOverrideCost: fragment.useOneTimeOverrideCost ?? false,
      useIntegrationPortCost: fragment.useIntegrationPortCost ?? false,
      channelDurationOverhead:
        stringToNumOpt(fragment.channelDurationOverhead) ?? null,
      bunkerOverrides: parseBunkerOverrides(fragment),
      bunkerPriceSource: parseBunkerPriceSource(fragment),
      weatherFactor: stringToNumOpt(fragment.weatherFactor) ?? null,
    };
  },
);

export const CargoAllocationFragmentToEntry = memoize(
  (
    fragment: ScenarioTimeline_CargoCompartmentAllocationsFragment,
  ): WithDbId<Required<CargoAllocation>, CargoCompartmentAllocationId> => {
    const {
      id,
      amount,
      orderId,
      clientId,
      compartmentId,
      scheduledOrderClientId,
    } = fragment;
    return {
      dbId: id,
      amount,
      orderId,
      compartmentId,
      clientId: parseClientId(clientId),
      scheduledOrderClientId: parseClientId(scheduledOrderClientId),
      archived: false,
    };
  },
);

const userLocallyDeletedScenariosSchema = zod.array(zod.string());

export const userLocallyDeletedScenariosAtomFamily = atomFamily(
  (userId: number | null) =>
    persistedAtom({
      key: `user_${userId ?? 'undefined'}_locally_deleted_scenarios`,
      schema: userLocallyDeletedScenariosSchema,
      defaultValue: [],
    }),
);

export const doDeleteEventFromScenario = (
  internal: InternalScenarioSchedule | EstimateTimeline | undefined,
  eventType: EventCategory.Single | EventCategory.OffHire,
  eventId: ClientId,
) => {
  if (!internal) {
    return console.error(`Cannot delete from scenario: no scenario selected`);
  }
  const { schedule } = internal;
  if (!schedule) {
    return console.error(`Cannot delete from scenario: no schedule present`);
  }
  if (!isScenarioMutationAllowed(internal)) {
    return mutatingReadonlyScenarioError();
  }

  // TODO split SingleEvent and OffHire to separate functions and data structures everywhere https://github.com/Seaber-io/frontend/pull/626#discussion_r703608114
  const eventIndex = schedule.findIndex(
    (event) =>
      event.clientId === eventId &&
      (eventType === EventCategory.Single // This comparison doesn't seem to be necessary anymore, client id's should be always unique anyway
        ? isSingleEntry(event)
        : isOffHireEntry(event)),
  );

  const event = schedule[eventIndex];

  if (event) {
    schedule.splice(eventIndex, 1);

    // Always add to deleted because this enables us to re-delete it when alternating scenarios
    internal.entryStates.set(eventId, EntryState.DELETED);
  } else {
    console.error('Could not find event to delete for id', eventId);
  }
};

const updateScenarioFromCargoAllocationChanges = (
  scenario: InternalUserSchedule | EstimateTimeline,
  changes: ChangedEntries['changedCargoAllocations'],
): void => {
  if (changes.size === 0) return;
  const now = Date.now();
  const { schedule } = scenario;

  // Get order assignments for setting scheduledOrderClientId
  const tcAssignmentByOrderId = mapKeyBy(
    schedule.filter(isTCOrderEntry),
    (e) => e.orderId,
  );

  scenario.cargoAllocations ??= [];
  const orderIdsWithChanges = new Set<OrderId>();

  for (const [clientId, cargoAllocation] of changes.entries()) {
    const oldIndex = scenario.cargoAllocations.findIndex(
      (entry) =>
        // clientId based matching for deletions
        entry.clientId === clientId ||
        // Updates / additions might have a different clientId (this shouldn't really happen, but there has been an issue before with an unknown cause (SEABER-5914))
        (cargoAllocation &&
          entry.orderId === cargoAllocation.orderId &&
          entry.compartmentId === cargoAllocation.compartmentId),
    );

    if (cargoAllocation === null || cargoAllocation.amount === 0) {
      // Deletion
      if (oldIndex !== -1) {
        const deletedAllocation = scenario.cargoAllocations[oldIndex];
        orderIdsWithChanges.add(deletedAllocation.orderId);
        scenario.cargoAllocations.splice(oldIndex, 1);
      }
    } else {
      // Addition or update
      orderIdsWithChanges.add(cargoAllocation.orderId);
      const assignment = tcAssignmentByOrderId.get(cargoAllocation.orderId);
      if (!assignment) {
        throw new Error(
          `Missing assignment in schedule for order ${cargoAllocation.orderId} and cargo allocation ${cargoAllocation.clientId}`,
        );
      }
      const allocationToSave = {
        ...cargoAllocation,
        scheduledOrderClientId: assignment.clientId,
      };
      if (oldIndex === -1) {
        scenario.cargoAllocations.push(allocationToSave);
      } else {
        scenario.cargoAllocations[oldIndex] = allocationToSave;
      }
    }
  }

  // Update change timestamps for affected order assignments
  for (const orderId of orderIdsWithChanges) {
    const assignment = tcAssignmentByOrderId.get(orderId);
    if (assignment) {
      const assignmentIndex = schedule.findIndex(
        (entry) =>
          isTCOrderEntry(entry) && entry.clientId === assignment.clientId,
      );
      if (assignmentIndex !== -1) {
        schedule[assignmentIndex] = {
          ...schedule[assignmentIndex],
          updatedAt: now,
        } as TCOrderEntry;
        scenario.entryStates.set(assignment.clientId, EntryState.UPDATED);
      }
    }
  }
};

function getScenarioOrEstimateTimeline(
  estimateIdOrScenario: ClientId | 'scenario',
  internal: InternalUserSchedule,
) {
  const scenario =
    estimateIdOrScenario !== 'scenario'
      ? internal.estimateTimelines?.find(
          (e) => e.clientId === estimateIdOrScenario,
        )
      : internal;
  if (!scenario) {
    throw new Error(`Cannot find scenario for id ${estimateIdOrScenario}`);
  }
  return scenario;
}

function withEstimateTimeline(
  internal: InternalScenarioSchedule | null | undefined,
  estimateId: ClientId,
  fn: (estimate: EstimateTimeline) => void,
): void {
  if (!internal || !isInternalUserScenario(internal)) return;

  const estimate = internal.estimateTimelines?.find(
    (et) => et.clientId === estimateId,
  );
  if (!estimate) return;

  fn(estimate);
  internal.changedAt = new Date().getTime();
}

export const updateScenarioFromChangeMaps = (
  internal: InternalUserSchedule,
  changes: ChangedEntries,
  estimateIdOrScenario: ClientId | 'scenario',
): InternalUserSchedule => {
  const now = Date.now();
  const scenario = getScenarioOrEstimateTimeline(
    estimateIdOrScenario,
    internal,
  );
  const {
    changedTCs,
    changedEvents,
    changedOffHires,
    changedCoas,
    changedSpots,
    changedPortCalls,
    deletedEvents,
    deletedPortCalls,
    changedCargoAllocations,
    changedOrderVoyageCharterIds,
  } = changes;
  const { schedule } = scenario;
  for (const [eventType, eventIds] of deletedEvents) {
    for (const eventId of eventIds) {
      doDeleteEventFromScenario(
        scenario,
        eventType === CoreEventType.Single
          ? EventCategory.Single
          : EventCategory.OffHire,
        eventId,
      );
    }
  }

  scenario.portCalls = scenario.portCalls.filter(
    (entry) => !deletedPortCalls.has(entry.clientId),
  );
  for (const eventId of deletedPortCalls) {
    scenario.entryStates.set(eventId, EntryState.DELETED);
  }

  function assertSameId(changeId: ClientId, entryId: ClientId) {
    if (changeId !== entryId) {
      throw new Error(
        `Entry id mismatch, change: ${changeId} entry: ${entryId}`,
      );
    }
  }

  const processed = new Set<Entry>();
  schedule.forEach((entry, assignmentIndex) => {
    const { clientId: entryId } = entry;
    if (isTCOrderEntry(entry)) {
      const change = changedTCs.get(entryId);
      if (!change) {
        return;
      }
      assertSameId(change.clientId, entryId);
      const newEntry: NoExtraProps<typeof entry, typeof change> = {
        ...entry,
        ...change,
        updatedAt: now,
      };

      checkFixEntryTimestamps(newEntry, change);
      schedule[assignmentIndex] = newEntry;
      scenario.entryStates.set(newEntry.clientId, EntryState.UPDATED);
      processed.add(change);
    } else if (isSingleEntry(entry)) {
      const { clientId: entryId } = entry;
      const change = changedEvents.get(entryId);
      if (!change) {
        return;
      }
      assertSameId(change.clientId, entryId);
      const newEntry: NoExtraProps<typeof entry, typeof change> = {
        ...entry,
        ...change,
        updatedAt: now,
      };
      if (change.duration) {
        newEntry.duration = change.duration;
        newEntry.end = change.start + change.duration;
      }
      schedule[assignmentIndex] = newEntry;
      scenario.entryStates.set(newEntry.clientId, EntryState.UPDATED);
      processed.add(change);
    } else if (isOffHireEntry(entry)) {
      const { clientId: entryId } = entry;
      const change = changedOffHires.get(entryId);
      if (!change) {
        return;
      }
      assertSameId(change.clientId, entryId);
      const newEntry: NoExtraProps<typeof entry, typeof entry> = {
        ...entry,
        ...change,
        updatedAt: now,
      };
      schedule[assignmentIndex] = newEntry;
      scenario.entryStates.set(newEntry.clientId, EntryState.UPDATED);
      processed.add(change);
    } else {
      exhaustiveCheck(entry);
    }
  });

  const numChanges = sumBy(Object.values(changes), (val) => val.size);

  if (processed.size === numChanges) {
    return internal;
  }

  const entries = [
    ...changedTCs.entries(),
    ...changedEvents.entries(),
    ...changedOffHires.entries(),
    ...changedCoas.entries(),
    ...changedSpots.entries(),
    ...changedPortCalls.entries(),
  ];
  for (const [, change] of entries) {
    if (processed.has(change)) {
      continue;
    }
    scenario.entryStates.set(change.clientId, EntryState.UPDATED);
    if (isSingleEntry(change)) {
      addNewEntryToScenario(scenario, change);
    } else if (isTCOrderEntry(change)) {
      addNewEntryToScenario(scenario, change);
    } else if (isOffHireEntry(change)) {
      addNewEntryToScenario(scenario, change);
    } else if (isSpotEntry(change)) {
      if (!('spotAssignments' in scenario)) {
        throw new Error(
          `Trying to update a spot assignment ${change.clientId} in estimate timeline`,
        );
      }
      const oldSpotIndex = scenario.spotAssignments.findIndex(
        (spot) => spot.clientId === change.clientId,
      );
      if (oldSpotIndex === -1) {
        addNewEntryToScenario(scenario, change);
        continue;
      }

      // noinspection UnnecessaryLocalVariableJS
      const newSpot: SpotEntry = {
        ...scenario.spotAssignments[oldSpotIndex],
        ...change,
        updatedAt: now,
      };
      scenario.spotAssignments[oldSpotIndex] = newSpot;

      scenario.entryStates.set(change.clientId, EntryState.UPDATED);
    } else if (isCoaEntry(change)) {
      if (!('coaAssignments' in scenario)) {
        throw new Error(
          `Trying to update a coa assignment ${change.clientId} in estimate timeline`,
        );
      }
      const oldCoaIndex = scenario.coaAssignments.findIndex(
        (coa) => coa.clientId === change.clientId,
      );
      if (oldCoaIndex === -1) {
        addNewEntryToScenario(scenario, change);
        continue;
      }

      // noinspection UnnecessaryLocalVariableJS
      const newCoa: CoaEntry = {
        ...scenario.coaAssignments[oldCoaIndex],
        ...change,
        updatedAt: now,
      };
      scenario.coaAssignments[oldCoaIndex] = newCoa;

      scenario.entryStates.set(change.clientId, EntryState.UPDATED);
    } else if (isPortCallEntry(change)) {
      const entries = scenario.portCalls;
      const oldEntryIndex = entries.findIndex(
        (entry) => entry.clientId === change.clientId,
      );
      const oldEntry = entries[oldEntryIndex];

      if (oldEntryIndex === -1) {
        addNewEntryToScenario(scenario, change);
      } else {
        const sourceChanges: Partial<PortCallEntry> = {};

        if (
          change.plannedArrivalOverride &&
          !change.plannedArrivalOverrideSourcedFrom &&
          oldEntry.plannedArrivalOverrideSourcedFrom
        ) {
          sourceChanges.plannedArrivalOverrideSourcedAt = now;
          sourceChanges.plannedArrivalOverrideSourcedFrom = null;
        }

        if (
          change.plannedBerthingOverride &&
          !change.plannedBerthingOverrideSourcedFrom &&
          oldEntry.plannedBerthingOverrideSourcedFrom
        ) {
          sourceChanges.plannedBerthingOverrideSourcedAt = now;
          sourceChanges.plannedBerthingOverrideSourcedFrom = null;
        }

        if (
          change.plannedDepartureOverride &&
          !change.plannedDepartureOverrideSourcedFrom &&
          oldEntry.plannedDepartureOverrideSourcedFrom
        ) {
          sourceChanges.plannedDepartureOverrideSourcedAt = now;
          sourceChanges.plannedDepartureOverrideSourcedFrom = null;
        }

        entries[oldEntryIndex] = {
          ...oldEntry,
          ...change,
          ...sourceChanges,
          updatedAt: now,
        };
      }
    } else {
      exhaustiveCheck(change);
    }
  }

  // Cargo allocations
  updateScenarioFromCargoAllocationChanges(scenario, changedCargoAllocations);

  for (const [orderId, voyageCharterId] of changedOrderVoyageCharterIds) {
    scenario.voyageCharterByOrderId.set(orderId, voyageCharterId);
  }

  internal.changedAt = new Date().getTime();
  return internal;
};

/**
 * Creates a patch map from old and new arrays, using a custom key function for indexing.
 * Only includes items that have actually changed, null values for deletions.
 */
const changeMapFromEntryArrays = <
  T extends { clientId: ClientId },
  K extends Narrowable[],
>(
  oldItems: T[],
  newItems: T[],
  getKey: (item: T) => K,
): Map<ClientId, T | null> => {
  const patch = new Map<ClientId, T | null>();

  const oldByKey = MultiKey.fromTuples(
    oldItems.map((item) => [getKey(item), item]),
  );

  const newByKey = MultiKey.fromTuples(
    newItems.map((item) => [getKey(item), item]),
  );

  // Find additions and updates
  newByKey.forEach((newItem, key) => {
    const oldItem = oldByKey.get(...key);
    if (!oldItem) {
      patch.set(newItem.clientId, newItem);
    } else if (!equal(oldItem, newItem)) {
      patch.set(oldItem.clientId, newItem);
    }
  });

  // Find deletions
  oldByKey.forEach((oldItem, key) => {
    const newItem = newByKey.get(...key);
    if (!newItem) {
      patch.set(oldItem.clientId, null);
    }
  });

  return patch;
};

/** The master client id is intentionally a value that is not a valid UUID but passes as one for our loose checks as a string. This prevents it from ever being accidentally saved to db by a developer error. */
export const MASTER_SCENARIO_CLIENTID: ClientId =
  parseClientId('masterscenario');

export type InternalScenarioSchedule =
  | InternalUserSchedule
  | InternalSuggestionSchedule
  | InternalMasterSchedule
  | InternalBackupSchedule
  | InternalInitialSchedule;

export const isInternalUserScenario = (
  s: InternalScenarioSchedule | null,
): s is InternalUserSchedule => s?.type === 'user';
export const isUserScenario = (
  s: Pick<ScenarioSchedule | EstimateTimeline, 'type'> | null,
): s is UserSchedule => s?.type === 'user';
export const isEstimateScenario = (
  s: Pick<ScenarioSchedule | EstimateTimeline, 'type'> | null,
): s is EstimateTimeline => s?.type === 'estimate';
export const isSuggestionScenario = (
  s: ScenarioSchedule | null,
): s is SuggestionSchedule => s?.type === 'suggestion';
export const isInternalMasterScenario = (
  s: InternalScenarioSchedule | null,
): s is InternalMasterSchedule => s?.type === 'master';
export const isMasterScenario = (
  s: ScenarioSchedule | null,
): s is MasterSchedule => s?.type === 'master';
export const isInternalBackupScenario = (
  s: InternalScenarioSchedule | null,
): s is InternalBackupSchedule => s?.type === 'backup';
export const isBackupScenario = (
  s: ScenarioSchedule | null,
): s is BackupSchedule => s?.type === 'backup';
export const isInternalInitialScenario = (
  s: InternalScenarioSchedule | null,
): s is InternalInitialSchedule => s?.type === 'initial';
export const isInitialScenario = (
  s: ScenarioSchedule | null,
): s is InitialSchedule => s?.type === 'initial';

export const toUserScenario = (
  scenario: InternalScenarioSchedule,
): UserSchedule => {
  return {
    ...scenario,
    id: null,
    migrationVersion: LATEST_MIGRATION_VERSION,
    entryStates: new Map(),
    type: 'user',
  };
};

export const buildCopiedScenario = (
  s: InternalScenarioSchedule | InternalBackupSchedule | null | undefined,
  opts: {
    currentUserId: UserId;
    scenarios: { name?: string | null }[];
    initials: string;
    keepName?: boolean;
    getDefaultScenario: () => InternalUserSchedule;
  },
): InternalUserSchedule => {
  if (!s) {
    return opts.getDefaultScenario();
  } else if (isUserScenario(s)) {
    return {
      ...s,
      id: null,
      isReadOnly: false,
      isIntegrationMaster: false,
      clientId: generateClientId(),
      createdBy: opts.currentUserId,
      entryStates: new Map(s.entryStates),
      name: opts.keepName
        ? s.name
        : generateScenarioName(opts.scenarios, opts.initials),
    };
  } else if ('backedUpAt' in s) {
    const { backedUpAt, ...rest } = s;
    return {
      ...opts.getDefaultScenario(),
      ...rest,
      type: 'user',
      isReadOnly: false,
      clientId: generateClientId(),
      createdBy: opts.currentUserId,
      entryStates: new Map(),
      name: opts.keepName
        ? s.name
        : generateScenarioName(opts.scenarios, opts.initials),
    };
  } else if (isSuggestionScenario(s)) {
    return {
      ...suggestionScenarioToUserScenario(s),
      name: opts.keepName
        ? s.name
        : generateScenarioName(opts.scenarios, opts.initials),
      clientId: generateClientId(),
      createdBy: opts.currentUserId,
      entryStates: new Map(s.entryStates),
    };
  } else {
    return {
      ...opts.getDefaultScenario(),
      ...s,
      type: 'user',
      isReadOnly: false,
      clientId: generateClientId(),
      entryStates: new Map(),
      name: opts.keepName
        ? s.name
        : generateScenarioName(opts.scenarios, opts.initials),
    };
  }
};

/** Fields which are stored as JSON in DB. The rest of the scenario fields are defined as columns in the scenarios table.
 * TODO: Ensure that every field in InternalUserSchedule is listed either here or the scenarios table.
 */
const SCENARIO_DATA_FIELDS = [
  'schedule',
  'spotAssignments',
  'coaAssignments',
  'portCalls',
  'patchOrders',
  'orderShortlist',
  'vesselsShortlist',
  'voyageCharterByOrderId',
  'description',
  'entryStates',
  'syncedWithMasterAt',
  'masterCommitAt',
  'cargoAllocations',
  'migrationVersion',
  'isReadOnly',
  'isIntegrationMaster',
  'autoUpdateFromMaster',
  'integrationData',
  'patchShipments',
  'importedAt',
  'estimateTimelines',
  'freightByShipmentClientId',
  'extraFreightByShipmentClientId',
] as const;

type ScenarioDataFields = (typeof SCENARIO_DATA_FIELDS)[number] &
  keyof InternalUserSchedule;

/** Return a good estimate of when a schedule has last updated based on timestamps on the schedule entries.
 * The returned value is the highest updatedAt value of any schedule entry.
 * This is usually sufficient but there may be some rare situations where for example the only change is changing the shipmentId on an order, which would not be registered in any timestamps.
 */
export const getScenarioUpdatedAt = (s: ScenarioSchedule): number => {
  const allAssignments = [
    ...s.schedule,
    ...s.spotAssignments,
    ...s.coaAssignments,
  ];
  return Math.max(
    dateStringToNumOpt(s.updatedAt) ?? 0,
    ...allAssignments.map((e) => e.updatedAt).filter(isDefined),
  );
};

export const generateScenarioName = (
  existingScenarios: { name?: string | null }[],
  initialName: string,
): string => {
  const getWeekNumber = () => {
    const now = new Date();
    const onejan = new Date(now.getFullYear(), 0, 1);
    const weekNum = Math.ceil(
      ((now.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7,
    );
    return weekNum.toString().padStart(2, '0');
  };

  const weekNum = getWeekNumber();

  const relevantScenarios = existingScenarios
    .map((scenario) => scenario.name)
    .filter((name) => name && name.startsWith(`${initialName}-W${weekNum}`));

  const highestNum = relevantScenarios.reduce((max, name) => {
    const num = parseInt(name!.split('-')[2], 10);
    return num > max ? num : max;
  }, 0);

  const newNum = (highestNum + 1).toString().padStart(3, '0');

  return `${initialName}-W${weekNum}-${newNum}`;
};

function filterDuplicateCargoAllocations(arr: CargoAllocation[]): void {
  const seenByOrderIdByCompartmentId = new DefaultMap<number, Set<number>>(
    () => new Set(),
  );

  const filtered = arr.filter(
    ({ orderId, compartmentId }: CargoAllocation): boolean => {
      const alreadySeen = seenByOrderIdByCompartmentId
        .get(orderId)
        .has(compartmentId);
      if (alreadySeen) {
        const errMsg = `Order #${orderId} had a duplicate cargo allocation on compartment ${compartmentId}. Duplicates have been removed, please verify the remaining one.`;
        console.error(errMsg, new Error().stack);
        toast.error(errMsg);
        return false;
      } else {
        seenByOrderIdByCompartmentId.get(orderId).add(compartmentId);
      }
      return true;
    },
  );

  if (arr.length !== filtered.length) {
    replaceContents(arr, filtered);
  }
}

/** When everything is working as expected, this function does nothing.
 * Verifies some assumptions we have about the scenario data, e.g. an order cannot be assigned twice.
 * If something is broken, logs & toast an error and mutates the input. Returns the mutated input. */
function verifyScenarioIntegrity<T extends InternalScenarioSchedule>(
  scenario: T,
): T {
  const assignedOrders = new Map<number, CargoEntry>();

  const filterDuplicateAssignments = (arr: Entry[]): void => {
    const filtered = arr.filter((entry: Entry): boolean => {
      if (!isCargoEntry(entry)) {
        return true;
      }
      const alreadyAssigned = assignedOrders.has(entry.orderId);
      if (alreadyAssigned) {
        const errMsg = `Order #${entry.orderId} has a duplicate ${entry.category} assignment on vessel ${entry.vesselId}. Duplicates have been removed, please verify the remaining one.`;
        console.error(errMsg);
        toast.error(errMsg);
        return false;
      } else {
        assignedOrders.set(entry.orderId, entry);
      }
      return true;
    });

    if (arr.length !== filtered.length) {
      replaceContents(arr, filtered);
    }
  };

  filterDuplicateAssignments(scenario.schedule);
  filterDuplicateAssignments(scenario.coaAssignments);
  filterDuplicateAssignments(scenario.spotAssignments);

  filterDuplicateCargoAllocations(scenario.cargoAllocations);

  const checkEventMinimumDurationOverrides = (arr: Entry[]): void => {
    // If any of these checks fail we have a bug in entry creation or updates.
    // One such was related to SEABER-5087 where a 0 mass cargo was derived to have 0 duration in backend scenario generation.
    for (const entry of arr) {
      if (isTCOrderEntry(entry)) {
        if (
          entry.loadingDurationOverride !== null &&
          entry.loadingDurationOverride < minimumEventDuration
        ) {
          console.warn(
            `Loading duration override violates minimum duration for order ${entry.orderId} in scenario ${scenario.name}`,
          );
        }
        if (
          entry.dischargingDurationOverride !== null &&
          entry.dischargingDurationOverride < minimumEventDuration
        ) {
          console.warn(
            `Discharging duration override violates minimum duration for order ${entry.orderId} in scenario ${scenario.name}`,
          );
        }
      } else if (isSingleEntry(entry)) {
        if (entry.duration < minimumEventDuration) {
          console.warn(
            `Event duration override violates minimum duration for single event ${entry.clientId} in scenario ${scenario.name}`,
          );
        }
      }
    }
  };

  checkEventMinimumDurationOverrides(scenario.schedule);

  return scenario;
}

export const parseScenario = (
  scenario: Scenarios_ScenarioFragment,
): InternalUserSchedule | null => {
  try {
    if (scenario.dataSchemaVersion !== SCENARIO_DATA_SCHEMA_VERSION) {
      toast.warn(
        `The scenario ${scenario.name} from ${scenario.createdAt} could not be loaded because it was saved with an older version of this app.`,
      );
      return null;
    }

    const committedData: Pick<InternalUserSchedule, ScenarioDataFields> =
      parseWithMaps(scenario.data);

    const {
      shared,
      updatedAt,
      clientId,
      dataSchemaVersion,
      deletedAt,
      createdAt,
      updatedBy,
      createdBy,
      name,
      id,
      organizationId,
      __typename,
    } = scenario;
    return verifyScenarioIntegrity({
      shared,
      updatedAt,
      dataSchemaVersion,
      deletedAt,
      createdAt,
      name,
      id,
      organizationId,
      __typename,
      type: 'user',
      clientId: parseClientId(clientId),
      createdBy: assertedDefined(createdBy, 'Expected createdBy to be present'),
      updatedBy: assertedDefined(updatedBy, 'Expected updatedBy to be present'),
      ...committedData,
    });
  } catch (e) {
    console.error(
      `Failed to parse scenario data for scenario ${scenario.id}:`,
      e,
    );
    return null;
  }
};

export const serializeScenario = (
  scenario: InternalUserSchedule | InternalMasterSchedule,
) => {
  return serializeWithMaps(pick(scenario, SCENARIO_DATA_FIELDS));
};

const createScenarioInput = (
  scenario: InternalUserSchedule,
): Required<ScenarioInput> => {
  const {
    createdBy,
    createdAt,
    clientId,
    updatedBy,
    updatedAt,
    dataSchemaVersion,
    deletedAt,
    shared,
    organizationId,
    name,
    description,
  } = scenario;
  const result = {
    createdBy,
    clientId,
    updatedBy,
    dataSchemaVersion,
    shared,
    organizationId,
    name: name ?? null,
    description: description ?? null,
    createdAt: createdAt ?? null,
    updatedAt: updatedAt ?? null,
    data: serializeScenario(scenario),
    deletedAt: deletedAt ?? null,
  };
  return exactProps<ScenarioInput, typeof result>(result);
};

export interface InternalScenarioState {
  scenarios: InternalScenarioSchedule[];
  selectedScenarioCID: ClientId | null;
  isIntegrationScenarioSelected: boolean;
  undoStack: UndoTransaction[];
  redoStack: UndoTransaction[];
  currentTransaction: ClientId | null;
}

type ScenarioUpdateSource = 'localsave' | 'commit' | 'scenario';

export type SingleScenarioAction =
  | {
      type: 'CREATE_ASSIGNMENT';
      assignment: Entry;
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'DELETE_ASSIGNMENT';
      orderId: Order['id'];
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | DeleteEventAction
  | {
      type: 'CREATE_AND_UPDATE_ASSIGNMENTS';
      changed: ChangedEntries;
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'UPDATE_ASSIGNMENT_SHARING';
      category: EventCategory;
      assignmentId: ClientId;
      shared: boolean;
    }
  | {
      type: 'UPDATE_ASSIGNMENTS_SHARING';
      ids: Set<ClientId>;
      shared: boolean;
    }
  | {
      type: 'SET_ORDER_VOYAGE_CHARTER';
      orderId: OrderId;
      voyageCharterId: VoyageCharterId | string | null;
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'SET_VOYAGE_CHARTER_FOR_ORDERS';
      orders: ScenarioOrder[];
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'UPDATE_ORDER';
      orderId: OrderId;
      patch: ScenarioOrderPatch['patch'];
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'SAVE_ALLOCATIONS';
      allocations: CargoAllocation[];
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'PATCH_SHIPMENT';
      clientId: ClientId;
      patch: PatchShipment;
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'SET_SCENARIO_AUTO_SYNC';
      newValue: InternalScenarioSchedule['autoUpdateFromMaster'];
    }
  | {
      type: 'TOGGLE_SHORTLIST_ORDERS';
      orderIds: OrderId[];
      shortlisted: boolean;
    }
  | {
      type: 'SET_SHORTLIST';
      orderIds: OrderId[];
    }
  | {
      type: 'TOGGLE_SHORTLIST_VESSEL';
      vesselId: ShipId;
    }
  | {
      type: 'SET_SHORTLIST_VESSELS';
      vesselIds: ShipId[];
    }
  | {
      type: 'SET_ESTIMATE_FREIGHT_OVERRIDES';
      estimateId: ClientId;
      overridesByOrderId: FreightRateOverridesByOrderId;
    }
  | {
      type: 'ADD_RESERVATION_TO_ESTIMATE';
      estimateId: ClientId;
      vesselReservation: VesselReservationFragment;
    }
  | {
      type: 'ADD_OPEN_POSITION_TO_ESTIMATE';
      estimateId: ClientId;
      openPosition: OpenPositionsFragment;
    }
  | {
      type: 'REMOVE_RESERVATION_FROM_ESTIMATE';
      estimateId: ClientId;
      vesselReservationId: number;
    }
  | {
      type: 'REMOVE_OPEN_POSITION_FROM_ESTIMATE';
      estimateId: ClientId;
      openPositionId: number;
    }
  | {
      type: 'SET_ESTIMATE_SHIPMENT_FREIGHT_OVERRIDES';
      estimateId: ClientId;
      overridesByShipmentId: ShipmentFreightOverridesByShipmentId;
    }
  | {
      type: 'SET_SHIPMENT_FREIGHT';
      shipmentClientId: ClientId;
      freight: { freightAmount: number; freightUnit: PaymentUnit } | null;
    }
  | {
      type: 'SET_SHIPMENT_EXTRA_FREIGHT';
      shipmentClientId: ClientId;
      newEntries: [
        {
          id: number | null;
          shipmentDbId: number | null;
          clientId: ClientId;
          name: string;
          freightAmount: number;
          freightUnit: PaymentUnit;
          description: string;
        },
      ];
    };
type UndoAction =
  | ({ type: 'BEGIN_UNDO_TRANSACTION' } & TransactionParams)
  | { type: 'END_UNDO_TRANSACTION'; transactionId?: ClientId }
  | { type: 'UNDO_STEP' }
  | { type: 'ROLLBACK_UNDO_TRANSACTION'; transactionId: ClientId }
  | { type: 'REDO_STEP' }
  | { type: 'CLEAR_UNDO_STACK' };

export type ScenarioAction =
  | SingleScenarioAction
  | UndoAction
  | {
      type: 'SET_SCENARIOS';
      scenarios:
        | ScenarioSchedule[]
        | ((oldScenarios: ScenarioSchedule[]) => ScenarioSchedule[]);
    }
  | {
      type: 'UPDATE_SCENARIOS';
      scenarios: InternalScenarioSchedule[];
      source?: ScenarioUpdateSource;
    }
  | {
      type: 'UPDATE_SCENARIOS_FROM_COMMIT';
      masterScenario: InternalMasterSchedule;
      updateUserScenarios: boolean;
      updateScenarioPortCallTimes: boolean;
    }
  | { type: 'SELECT_SCENARIO'; scenarioCID: ClientId | null }
  | {
      type: 'CREATE_SCENARIO';
      scenario: ScenarioSchedule;
      isDuplicated: boolean;
    }
  | { type: 'DELETE_SCENARIO'; scenarioCID: ScenarioSchedule['clientId'] }
  | {
      type: 'MERGE_SCENARIO';
      sourceCID: ScenarioSchedule['clientId'];
      targetCID: ScenarioSchedule['clientId'];
    }
  | {
      type: 'RESET_ASSIGNMENTS_TO_MASTER';
      orderIds: OrderId[];
      estimateIdOrScenario: ClientId | 'scenario';
    }
  | {
      type: 'CREATE_ESTIMATE_TIMELINE';
      vesselId: ShipId;
      newEstimateId: ClientId | undefined;
      oldEstimateIdOrScenario: ClientId | 'scenario';
      currentVesselReservations: VesselReservationFragment[];
      currentOpenPositions: OpenPositionsFragment[];
    }
  | {
      type: 'APPLY_ESTIMATE_TIMELINE';
      estimateId: ClientId;
      keepEstimates: boolean;
    }
  | {
      type: 'DELETE_ESTIMATE_TIMELINE';
      estimateId: ClientId;
    }
  | {
      type: 'SET_ESTIMATE_FREIGHT_OVERRIDES';
      estimateId: ClientId;
      overridesByOrderId: FreightRateOverridesByOrderId;
    }
  | {
      type: 'ADD_RESERVATION_TO_ESTIMATE';
      estimateId: ClientId;
      vesselReservation: VesselReservationFragment;
    }
  | {
      type: 'REMOVE_RESERVATION_FROM_ESTIMATE';
      estimateId: ClientId;
      vesselReservationId: number;
    }
  | {
      type: 'REMOVE_OPEN_POSITION_FROM_ESTIMATE';
      estimateId: ClientId;
      openPositionId: number;
    }
  | {
      type: 'SET_ESTIMATE_SHIPMENT_FREIGHT_OVERRIDES';
      estimateId: ClientId;
      overridesByShipmentId: ShipmentFreightOverridesByShipmentId;
    };

export const isCommitted = (
  scenario: ScenarioSchedule,
  event: Events,
): boolean => {
  return (
    isMasterScenario(scenario) ||
    isBackupScenario(scenario) ||
    isInitialScenario(scenario) ||
    !scenario.entryStates.has(event.entry.clientId)
  );
};

/** Set a new patch object or merges new values into an existing one */
const updateOrderPatch = (
  patches: Map<number, ScenarioOrderPatch>,
  patch: ScenarioOrderPatch,
): void => {
  const orderId = patch.id;
  const existingPatch = patches.get(orderId);
  if (
    (existingPatch?.type === 'update' || existingPatch?.type === 'create') &&
    'patch' in patch
  ) {
    patches.set(orderId, {
      ...existingPatch,
      patch: { ...existingPatch?.patch, ...patch.patch },
    });
  } else {
    patches.set(orderId, patch);
  }
};

const mutatingReadonlyScenarioError = (() => {
  return (msg = '') => {
    toast.error(
      `Altering a readonly scenario is not allowed. Create a new editable scenario first.`,
      {
        toastId: 'mutatingReadonlyScenarioError',
      },
    );
    console.error(
      `Trying to mutate readonly scenario, this is not allowed. ${msg}`,
    );
  };
})();

const isScenarioMutationAllowed = (
  s: InternalScenarioSchedule | EstimateTimeline | undefined,
): s is InternalUserSchedule | EstimateTimeline => {
  return !!s && ((s.type === 'user' && !s.isReadOnly) || s.type === 'estimate');
};

export const addNewEntryToScenario = (
  scenario: InternalUserSchedule | EstimateTimeline,
  entry: Entry,
  updateStates = true,
): void => {
  const { schedule } = scenario;

  if (isCargoEntry(entry)) {
    const existingAssignments = getOrderAssignments(scenario);
    if (existingAssignments.has(entry.orderId)) {
      const msg = `Trying to create a new assignment for order #${
        entry.orderId
      } but it is already assigned onto vessel #${entry.vesselId} at ${new Date(
        entry.loadingStart,
      )}`;
      toast.error(msg, {
        toastId: 'AssigningAlreadyAssignedOrder',
      });
      console.error(msg);
      return;
    }
  }

  if (updateStates) {
    entry = {
      ...entry,
      updatedAt: Date.now(),
    };

    scenario.entryStates.set(entry.clientId, EntryState.UPDATED);
  }

  if (isTCOrderEntry(entry)) {
    schedule.push(entry);
  } else if (isSingleEntry(entry)) {
    schedule.push(entry);
  } else if (isOffHireEntry(entry)) {
    schedule.push(entry);
  } else if (isSpotEntry(entry)) {
    if (!('spotAssignments' in scenario)) {
      throw new Error(
        `Trying to create a new spot assignment for order #${entry.orderId} in estimate timeline`,
      );
    }
    scenario.spotAssignments.push(entry);
  } else if (isCoaEntry(entry)) {
    if (!('coaAssignments' in scenario)) {
      throw new Error(
        `Trying to create a new coa assignment for order #${entry.orderId} in estimate timeline`,
      );
    }
    scenario.coaAssignments.push(entry);
  } else if (isPortCallEntry(entry)) {
    scenario.portCalls.push(entry);
  } else {
    exhaustiveCheck(entry);
  }
};

export const findByClientId = <T extends { clientId: ClientId }>(
  xs: readonly T[],
  cid: ClientId | null,
): T | undefined => xs.find((x) => x.clientId === cid);

export const buildDeletedAssignmentPatch = (
  orderId: OrderId,
  clearShipmentId: boolean = true,
): ScenarioOrderPatch => ({
  id: orderId,
  type: 'update',
  patch: {
    orderPortCallRelationsByOrderId: { nodes: [] },
    ...(clearShipmentId ? { shipmentId: null } : {}),
  },
});

const removeAssignmentFromScenario = (
  scenario: InternalUserSchedule | EstimateTimeline,
  internal: InternalUserSchedule,
  orderId: OrderId,
  {
    isDelete = false,
    warnMissing = true,
  }: {
    /** Is this an explicit delete action. false means we're just cleaning up a scenario for whatever reason, e.g. a merge */
    isDelete?: boolean;
    warnMissing?: boolean;
  } = {},
) => {
  const { schedule } = scenario;
  const assignmentIndex = schedule.findIndex(
    (entry) => 'orderId' in entry && entry.orderId === orderId,
  );
  const handledBySpotIndex =
    'spotAssignments' in scenario
      ? scenario.spotAssignments.findIndex((spot) => spot.orderId === orderId)
      : -1;
  const coaAssignmentIndex =
    'coaAssignments' in scenario
      ? scenario.coaAssignments.findIndex(
          (coaAssignment) => coaAssignment.orderId === orderId,
        )
      : -1;

  let entry: Entry | null = null;

  if (assignmentIndex > -1) {
    entry = schedule[assignmentIndex];
    schedule.splice(assignmentIndex, 1);
  } else if (handledBySpotIndex > -1 && 'spotAssignments' in scenario) {
    entry = scenario.spotAssignments[handledBySpotIndex];
    scenario.spotAssignments.splice(handledBySpotIndex, 1);
  } else if (coaAssignmentIndex > -1 && 'coaAssignments' in scenario) {
    entry = scenario.coaAssignments[coaAssignmentIndex];
    scenario.coaAssignments.splice(coaAssignmentIndex, 1);
  } else {
    if (warnMissing) {
      console.warn(
        'Could not find assignment to delete for order id',
        orderId,
        'This is usually fine when moving spot orders.',
      );
    }
  }

  if (
    scenario.cargoAllocations?.find(
      (allocation) => allocation.orderId === orderId,
    )
  ) {
    scenario.cargoAllocations = scenario.cargoAllocations.filter(
      (allocation) => allocation.orderId !== orderId,
    );
  }

  if (entry && isDelete) {
    scenario.entryStates.set(entry.clientId, EntryState.DELETED);
  } else if (entry) {
    scenario.entryStates.delete(entry.clientId);
  }

  // Never clear shipmentId here - that's handled by setOrderShipment when needed
  updateOrderPatch(
    scenario.patchOrders,
    buildDeletedAssignmentPatch(orderId, false),
  );
  internal.changedAt = new Date().getTime();
};

export const singleScenarioReducer = (
  internal: InternalScenarioSchedule,
  action: SingleScenarioAction,
): void => {
  const internalSchedule = internal?.schedule;

  if (!isScenarioMutationAllowed(internal) || !internalSchedule) {
    mutatingReadonlyScenarioError();
    return;
  }

  switch (action.type) {
    case 'CREATE_ASSIGNMENT': {
      const scenario = getScenarioOrEstimateTimeline(
        action.estimateIdOrScenario,
        internal,
      );
      addNewEntryToScenario(scenario, action.assignment);
      internal.changedAt = new Date().getTime();
      break;
    }
    case 'SET_ESTIMATE_FREIGHT_OVERRIDES': {
      withEstimateTimeline(internal, action.estimateId, (estimate) => {
        estimate.freightRateOverridesByOrderId = action.overridesByOrderId;
      });
      break;
    }
    case 'ADD_RESERVATION_TO_ESTIMATE': {
      withEstimateTimeline(internal, action.estimateId, (estimate) => {
        estimate.vesselReservations = [
          ...(estimate.vesselReservations ?? []),
          toEstimateVesselReservation(action.vesselReservation),
        ];
      });
      break;
    }
    case 'ADD_OPEN_POSITION_TO_ESTIMATE': {
      withEstimateTimeline(internal, action.estimateId, (estimate) => {
        estimate.openPositions = [
          ...(estimate.openPositions ?? []),
          toEstimateOpenPosition(action.openPosition),
        ];
      });
      break;
    }
    case 'REMOVE_RESERVATION_FROM_ESTIMATE': {
      withEstimateTimeline(internal, action.estimateId, (estimate) => {
        if (!estimate.vesselReservations) return;

        estimate.vesselReservations = estimate.vesselReservations.filter(
          (vr) => vr.id !== action.vesselReservationId,
        );
      });
      break;
    }
    case 'REMOVE_OPEN_POSITION_FROM_ESTIMATE': {
      withEstimateTimeline(internal, action.estimateId, (estimate) => {
        if (!estimate.openPositions) return;

        estimate.openPositions = estimate.openPositions.filter(
          (op) => op.id !== action.openPositionId,
        );
      });
      break;
    }
    case 'SET_ESTIMATE_SHIPMENT_FREIGHT_OVERRIDES': {
      if (!internal || !isInternalUserScenario(internal)) {
        return;
      }
      if (!internal.estimateTimelines) return;

      const estimateIndex = internal.estimateTimelines.findIndex(
        (et) => et.clientId === action.estimateId,
      );

      if (estimateIndex === -1) return;

      // Create new array reference so React detects the change
      internal.estimateTimelines = [...internal.estimateTimelines];
      internal.estimateTimelines[estimateIndex] = {
        ...internal.estimateTimelines[estimateIndex],
        shipmentFreightOverridesByShipmentId: action.overridesByShipmentId,
      };
      internal.changedAt = new Date().getTime();
      break;
    }
    case 'CREATE_AND_UPDATE_ASSIGNMENTS': {
      updateScenarioFromChangeMaps(
        internal,
        action.changed,
        action.estimateIdOrScenario,
      );
      break;
    }
    case 'DELETE_ASSIGNMENT': {
      const { orderId, estimateIdOrScenario } = action;
      const scenario = getScenarioOrEstimateTimeline(
        estimateIdOrScenario,
        internal,
      );
      removeAssignmentFromScenario(scenario, internal, orderId, {
        isDelete: true,
      });
      break;
    }
    case 'DELETE_EVENT': {
      const estimateIdOrScenario = action.estimateIdOrScenario;
      const scenario = getScenarioOrEstimateTimeline(
        estimateIdOrScenario,
        internal,
      );
      doDeleteEventFromScenario(scenario, action.eventType, action.eventId);
      if (internal) internal.changedAt = new Date().getTime();
      break;
    }
    case 'UPDATE_ASSIGNMENT_SHARING': {
      const { assignmentId, category, shared } = action;

      if (category === EventCategory.TC) {
        const { schedule } = internal;
        const assignmentIndex = schedule.findIndex(
          (entry) => 'orderId' in entry && entry.clientId === assignmentId,
        );

        if (assignmentIndex > -1) {
          schedule[assignmentIndex].shared = shared;
        }
      } else if (category === EventCategory.COA) {
        const assignmentIndex = internal.coaAssignments.findIndex(
          (coaAssignment) => coaAssignment.clientId === assignmentId,
        );

        if (assignmentIndex > -1) {
          internal.coaAssignments[assignmentIndex].shared = shared;
        }
      } else if (category === EventCategory.Spot) {
        const assignmentIndex = internal.spotAssignments.findIndex(
          (spot) => spot.clientId === assignmentId,
        );

        if (assignmentIndex > -1) {
          internal.spotAssignments[assignmentIndex].shared = shared;
        }
      }

      internal.changedAt = new Date().getTime();
      break;
    }
    case 'UPDATE_ASSIGNMENTS_SHARING': {
      const { ids, shared } = action;
      for (const a of internal.schedule) {
        if (ids.has(a.clientId)) {
          a.shared = shared;
        }
      }
      for (const a of internal.coaAssignments) {
        if (ids.has(a.clientId)) {
          a.shared = shared;
        }
      }
      for (const a of internal.spotAssignments) {
        if (ids.has(a.clientId)) {
          a.shared = shared;
        }
      }
      internal.changedAt = new Date().getTime();
      break;
    }
    case 'SET_ORDER_VOYAGE_CHARTER': {
      const { orderId, voyageCharterId, estimateIdOrScenario } = action;
      const scenario = getScenarioOrEstimateTimeline(
        estimateIdOrScenario,
        internal,
      );
      scenario.voyageCharterByOrderId.set(orderId, voyageCharterId);
      break;
    }
    case 'SET_VOYAGE_CHARTER_FOR_ORDERS': {
      const { orders, estimateIdOrScenario } = action;
      const scenario = getScenarioOrEstimateTimeline(
        estimateIdOrScenario,
        internal,
      );
      const voyageCharterIds = orders.map((order) => {
        const scenarioCharter = scenario.voyageCharterByOrderId.get(order.id);

        // If voyageCharterId for the order is set null in scenario we can
        // assume that the cached voyage charter relation should be ignored
        return scenarioCharter !== undefined
          ? scenarioCharter
          : order.outgoingVoyageCharterRelation?.voyageCharterId;
      });

      const existingVoyageCharterIds = voyageCharterIds.filter(
        (voyageCharterId) => typeof voyageCharterId === 'number',
      );

      const createdVoyageCharterIds = voyageCharterIds.filter(
        (voyageCharterId) => typeof voyageCharterId === 'string',
      );

      const voyageCharterId =
        existingVoyageCharterIds[0] ??
        createdVoyageCharterIds[0] ??
        generateClientId();

      orders.forEach((order) => {
        scenario.voyageCharterByOrderId.set(order.id, voyageCharterId);
      });
      break;
    }
    case 'UPDATE_ORDER': {
      const { orderId, patch, estimateIdOrScenario } = action;
      const scenario = getScenarioOrEstimateTimeline(
        estimateIdOrScenario,
        internal,
      );
      updateOrderPatch(scenario.patchOrders, {
        id: orderId,
        type: 'update',
        patch,
      });
      const { shipmentId } = patch;
      if (isDefined(shipmentId)) {
        scenario.entryStates.set(shipmentId, EntryState.UPDATED);
        if (!scenario.patchShipments.has(shipmentId)) {
          scenario.patchShipments.set(shipmentId, buildShipmentPatch());
        }
        for (const entry of internal.schedule) {
          if (isTCOrderEntry(entry) && entry.orderId === orderId) {
            entry.shipmentId = shipmentId;
          }
        }
      }
      internal.changedAt = new Date().getTime();
      break;
    }
    case 'SAVE_ALLOCATIONS': {
      const scenario = getScenarioOrEstimateTimeline(
        action.estimateIdOrScenario,
        internal,
      );
      // Use changemap so we can update updatedAt only for changed allocations
      const changes = changeMapFromEntryArrays(
        scenario.cargoAllocations,
        action.allocations,
        (allocation) => [allocation.orderId, allocation.compartmentId] as const,
      );
      updateScenarioFromCargoAllocationChanges(scenario, changes);
      internal.changedAt = new Date().getTime();
      break;
    }
    case 'PATCH_SHIPMENT': {
      const { clientId, patch } = action;
      const scenario = getScenarioOrEstimateTimeline(
        action.estimateIdOrScenario,
        internal,
      );
      scenario.entryStates.set(clientId, EntryState.UPDATED);
      scenario.patchShipments.set(clientId, patch);

      internal.changedAt = new Date().getTime();
      break;
    }
    case 'SET_SCENARIO_AUTO_SYNC': {
      const { newValue } = action;
      internal.autoUpdateFromMaster = newValue;
      break;
    }
    case 'TOGGLE_SHORTLIST_ORDERS': {
      const { orderIds, shortlisted } = action;
      const currentShortlist = internal.orderShortlist?.orderIds ?? [];

      const newShortList = shortlisted
        ? union(currentShortlist, orderIds)
        : difference(currentShortlist, orderIds);

      internal.orderShortlist = {
        orderIds: newShortList,
        updatedAt: Date.now(),
      };
      internal.changedAt = new Date().getTime();
      break;
    }
    case 'SET_SHORTLIST': {
      const { orderIds } = action;
      internal.orderShortlist = {
        orderIds,
        updatedAt: Date.now(),
      };
      internal.changedAt = new Date().getTime();
      break;
    }
    case 'TOGGLE_SHORTLIST_VESSEL':
      const { vesselId } = action;

      const currentShortlist = internal.vesselsShortlist?.vesselIds ?? [];
      const shortlisted = currentShortlist.includes(vesselId);
      const newShortList = shortlisted
        ? currentShortlist.filter((v) => v != vesselId)
        : [...currentShortlist, vesselId];

      internal.vesselsShortlist = {
        vesselIds: newShortList,
        updatedAt: Date.now(),
      };
      internal.changedAt = new Date().getTime();
      break;
    case 'SET_SHORTLIST_VESSELS':
      const { vesselIds } = action;
      internal.vesselsShortlist = {
        vesselIds,
        updatedAt: Date.now(),
      };
      internal.changedAt = new Date().getTime();
      break;
    case 'SET_SHIPMENT_FREIGHT': {
      const { shipmentClientId, freight } = action;

      if (freight) {
        internal.freightByShipmentClientId.set(shipmentClientId, {
          clientId: generateClientId(),
          shipmentDbId: null,
          id: null,
          ...freight,
        });
      } else {
        internal.freightByShipmentClientId.delete(shipmentClientId);
      }

      internal.changedAt = new Date().getTime();
      break;
    }
    case 'SET_SHIPMENT_EXTRA_FREIGHT': {
      const { shipmentClientId, newEntries } = action;

      internal.extraFreightByShipmentClientId.set(shipmentClientId, newEntries);
      internal.changedAt = new Date().getTime();
      break;
    }
    default:
      exhaustiveCheck(action);
  }
};

const scenarioReducer = (
  draft: InternalScenarioState,
  action: Exclude<ScenarioAction, UndoAction>,
): void => {
  const internal = findByClientId(draft.scenarios, draft.selectedScenarioCID);

  const userScenarioCountBefore = draft.scenarios.filter(
    (s) => !isMasterScenario(s),
  ).length;

  switch (action.type) {
    case 'SET_SCENARIOS': {
      const rawScenarios: InternalScenarioSchedule[] = castDraft(
        typeof action.scenarios === 'function'
          ? action.scenarios(draft.scenarios)
          : action.scenarios,
      );
      // Prune unused port calls to prevent accumulation across commits
      const newScenarios = rawScenarios.map(pruneUnusedPortCalls);
      if (newScenarios.length === draft.scenarios.length) {
        for (let i = 0; i < newScenarios.length; ++i) {
          draft.scenarios[i] = newScenarios[i];
        }
      } else {
        draft.scenarios = newScenarios;
      }
      break;
    }
    case 'UPDATE_SCENARIOS': {
      const oldScenariosById = mapKeyBy(draft.scenarios, (s) => s.clientId);
      const newScenariosById = mapKeyBy(action.scenarios, (s) => s.clientId);
      const allIds = new Set([
        ...oldScenariosById.keys(),
        ...newScenariosById.keys(),
      ]);

      const result: InternalScenarioSchedule[] = Array.from(
        allIds.values(),
      ).map((clientId) => {
        const oldScenario = oldScenariosById.get(clientId);
        const newScenario = newScenariosById.get(clientId);

        if (!oldScenario) {
          assertDefined(
            newScenario,
            `New scenario expected to exist when old doesn't. Logically impossible not to.`,
          );
          return newScenario;
        } else if (!newScenario) {
          return oldScenario;
        } else if (
          isInternalBackupScenario(newScenario) ||
          isInternalBackupScenario(oldScenario)
        ) {
          // Never update backups with anything
          return oldScenario;
        } else if (isInternalMasterScenario(newScenario)) {
          // If we re-apply local save onto scenarios the committed (master) scenario's local state can never be better than what has been received from the APIs
          if (action.source === 'localsave') {
            return oldScenario;
          } else {
            return pruneUnusedPortCalls(newScenario);
          }
        } else {
          const merged = getMergedScenario(
            oldScenario,
            newScenario,
            MERGE_SOURCE_NOT_COMMIT,
          );
          if (action.source === 'localsave') {
            // localStorage `updatedAt` can be stale (older app versions inflated
            // it during merge). The DB-sourced value already in state wins.
            return { ...merged, updatedAt: oldScenario.updatedAt };
          }
          return merged;
        }
      });

      draft.scenarios = orderBy(result, (s) => s.createdAt);
      break;
    }
    case 'UPDATE_SCENARIOS_FROM_COMMIT': {
      const updated = updateScenariosFromCommitInternal(
        draft.scenarios,
        action,
      );
      for (let i = 0; i < updated.length; ++i) {
        draft.scenarios[i] = updated[i];
      }
      break;
    }
    case 'MERGE_SCENARIO': {
      const source = findByClientId(draft.scenarios, action.sourceCID);
      const target = findByClientId(draft.scenarios, action.targetCID);

      if (!source || !target) {
        console.error(
          `Source or target scenario not found for merge ${action}`,
        );
        break;
      }

      if (isBackupScenario(source)) {
        // Backups don't need any kind of updates
        break;
      }

      const targetIndex = draft.scenarios.findIndex(
        (s) => s.clientId === action.targetCID,
      );
      const merged = getMergedScenario(
        target,
        source,
        isMasterScenario(source)
          ? MERGE_SOURCE_IS_COMMIT
          : MERGE_SOURCE_NOT_COMMIT,
      );
      draft.scenarios[targetIndex] = pruneUnusedPortCalls(merged);
      break;
    }
    case 'CREATE_SCENARIO': {
      if (action.isDuplicated) {
        const newIndex =
          draft.scenarios.findIndex(
            (s) => s.clientId === draft.selectedScenarioCID,
          ) + 1;
        draft.scenarios = [
          ...draft.scenarios.slice(0, newIndex),
          action.scenario as InternalScenarioSchedule,
          ...draft.scenarios.slice(newIndex),
        ];
        draft.selectedScenarioCID = action.scenario.clientId;
      } else {
        draft.scenarios.push(action.scenario as InternalScenarioSchedule);
      }
      break;
    }
    case 'DELETE_SCENARIO': {
      const scenarioIndex = draft.scenarios.findIndex(
        (s) => s.clientId === action.scenarioCID,
      );
      draft.scenarios.splice(scenarioIndex, 1);
      break;
    }
    case 'SELECT_SCENARIO': {
      draft.selectedScenarioCID = action.scenarioCID;
      break;
    }
    case 'CREATE_ESTIMATE_TIMELINE': {
      if (!internal || !isInternalUserScenario(internal)) {
        return;
      }
      const estimates = internal.estimateTimelines ?? [];
      if (estimates.length === 0) {
        internal.estimateTimelines = estimates;
      }
      const vesselId = action.vesselId;
      const scenario = getScenarioOrEstimateTimeline(
        action.oldEstimateIdOrScenario,
        internal,
      );
      const sourceSchedule = scenario.schedule.filter(
        (s) => s.vesselId === vesselId,
      );

      const scheduledOrderIds = new Set(
        sourceSchedule.map((s) => ('orderId' in s ? s.orderId : null)),
      );
      scheduledOrderIds.delete(null);

      const scheduledClientIds = new Set(
        sourceSchedule.map((s) => ('clientId' in s ? s.clientId : null)),
      );
      scheduledClientIds.delete(null);

      const patchedOrders = Array.from(scenario.patchOrders.entries()).filter(
        (s) => scheduledOrderIds.has(s[0]),
      );

      const shipmentClientIds = new Set([
        ...patchedOrders.map(([_, patch]) =>
          'shipmentId' in patch ? patch.shipmentId : null,
        ),
        ...sourceSchedule.map((s) => ('shipmentId' in s ? s.shipmentId : null)),
      ]);
      shipmentClientIds.delete(null);

      const sourcePortCalls = scenario.portCalls.filter(
        (s) => s.vesselId === vesselId,
      );

      const pcClientIds = new Set(sourcePortCalls.map((s) => s.clientId));

      // Build entryStates for this estimate timeline
      const copiedEntryStates = new Map<ClientId, EntryState>();
      for (const [clientId, state] of scenario.entryStates?.entries() ?? []) {
        if (scheduledClientIds.has(clientId) || pcClientIds.has(clientId)) {
          copiedEntryStates.set(clientId, state);
        }
      }

      estimates.push({
        vesselId,
        clientId: action.newEstimateId ?? generateClientId(),
        type: 'estimate',
        schedule: sourceSchedule,
        cargoAllocations: scenario.cargoAllocations.filter((s) =>
          scheduledOrderIds.has(s.orderId),
        ),
        portCalls: sourcePortCalls,
        patchOrders: new Map(patchedOrders),
        patchShipments: new Map(
          Array.from(scenario.patchShipments.entries()).filter((s) =>
            shipmentClientIds.has(s[0]),
          ),
        ),
        entryStates: copiedEntryStates,
        voyageCharterByOrderId: new Map(
          Array.from(scenario.voyageCharterByOrderId?.entries() ?? []).filter(
            (e) => scheduledOrderIds.has(e[0]),
          ),
        ),
        vesselReservations: action.currentVesselReservations,
        openPositions: action.currentOpenPositions,
      });
      break;
    }
    case 'APPLY_ESTIMATE_TIMELINE': {
      if (!internal || !isInternalUserScenario(internal)) {
        return;
      }
      const estimates = internal.estimateTimelines;
      if (!estimates?.length) {
        return;
      }
      const estimate = estimates.find((e) => e.clientId === action.estimateId);
      if (!estimate) {
        return;
      }
      const vesselId = estimate.vesselId;
      const schedule = estimate.schedule;

      const scheduledOrderIds = new Set(
        schedule.map((s) => ('orderId' in s ? s.orderId : null)),
      );
      scheduledOrderIds.delete(null);

      const scheduledClientIds = new Set(
        schedule.map((s) => ('clientId' in s ? s.clientId : null)),
      );
      scheduledClientIds.delete(null);

      const patchedOrders = Array.from(estimate.patchOrders.entries());

      const shipmentClientIds = new Set([
        ...patchedOrders.map(([_, patch]) =>
          'shipmentId' in patch ? patch.shipmentId : null,
        ),
        ...schedule.map((s) => ('shipmentId' in s ? s.shipmentId : null)),
      ]);
      shipmentClientIds.delete(null);

      internal.schedule = [
        ...internal.schedule.filter(
          (s) =>
            s.vesselId !== vesselId &&
            (!('orderId' in s) || !scheduledOrderIds.has(s.orderId)),
        ),
        ...estimate.schedule,
      ];
      internal.cargoAllocations = [
        ...internal.cargoAllocations.filter(
          (c) => !scheduledOrderIds.has(c.orderId),
        ),
        ...estimate.cargoAllocations,
      ];
      internal.portCalls = [
        ...internal.portCalls.filter((s) => s.vesselId !== vesselId),
        ...estimate.portCalls,
      ];
      internal.patchOrders = new Map([
        ...Array.from(internal.patchOrders.entries()).filter(
          (s) => !scheduledOrderIds.has(s[0]),
        ),
        ...estimate.patchOrders.entries(),
      ]);
      internal.patchShipments = new Map([
        ...Array.from(internal.patchShipments.entries()).filter(
          (s) => !shipmentClientIds.has(s[0]),
        ),
        ...estimate.patchShipments.entries(),
      ]);
      internal.entryStates = new Map([
        ...Array.from(internal.entryStates?.entries() ?? []).filter(
          (e) => !scheduledClientIds.has(e[0]),
        ),
        ...estimate.entryStates.entries(),
      ]);
      internal.voyageCharterByOrderId = new Map([
        ...Array.from(internal.voyageCharterByOrderId?.entries() ?? []).filter(
          (e) => !scheduledOrderIds.has(e[0]),
        ),
        ...estimate.voyageCharterByOrderId.entries(),
      ]);

      // Apply shipment freight overrides from the estimate
      const shipmentFreightOverrides =
        estimate.shipmentFreightOverridesByShipmentId;
      if (shipmentFreightOverrides) {
        for (const [shipmentClientId, override] of shipmentFreightOverrides) {
          if (override.freight === null) {
            internal.freightByShipmentClientId.delete(shipmentClientId);
            continue;
          }

          const existing =
            internal.freightByShipmentClientId.get(shipmentClientId);
          if (existing) {
            existing.freightAmount = override.freight;
            existing.freightUnit = override.freightUnit;
          } else {
            internal.freightByShipmentClientId.set(shipmentClientId, {
              clientId: shipmentClientId,
              shipmentDbId: null,
              id: null,
              freightAmount: override.freight,
              freightUnit: override.freightUnit,
            });
          }
        }
      }

      if (action.keepEstimates) {
        break;
      }
      const estimateTimelines = estimates.filter(
        (e) => e.vesselId !== vesselId,
      );
      internal.estimateTimelines = estimateTimelines.length
        ? estimateTimelines
        : undefined;
      break;
    }
    case 'DELETE_ESTIMATE_TIMELINE': {
      if (!internal || !isInternalUserScenario(internal)) {
        return;
      }
      const estimates = internal.estimateTimelines;
      if (!estimates?.length) {
        return;
      }
      const estimateTimelines = estimates.filter(
        (e) => e.clientId !== action.estimateId,
      );
      internal.estimateTimelines = estimateTimelines.length
        ? estimateTimelines
        : undefined;
      break;
    }

    case 'RESET_ASSIGNMENTS_TO_MASTER': {
      // Master => committed
      if (!isScenarioMutationAllowed(internal) || !internal.schedule) {
        mutatingReadonlyScenarioError();
        break;
      }
      const { orderIds } = action;
      const masterScenario = draft.scenarios.find(isMasterScenario);
      assertDefined(
        masterScenario,
        `Committed scenario is not available while resetting assignment for orders ${orderIds.join(
          ',',
        )} to committed assignment`,
      );

      const relatedPortCallCIds: Set<ClientId> = new Set();

      const scenario = getScenarioOrEstimateTimeline(
        action.estimateIdOrScenario,
        internal,
      );
      for (const orderId of orderIds) {
        removeAssignmentFromScenario(scenario, internal, orderId, {
          warnMissing: false,
        });

        const masterAssignment =
          getOrderAssignments(masterScenario).get(orderId);
        if (masterAssignment) {
          addNewEntryToScenario(internal, masterAssignment, false);
          const masterCargoAllocations =
            masterScenario.cargoAllocations?.filter(
              (alloc) => alloc.orderId === orderId,
            ) ?? [];
          internal.cargoAllocations = [
            ...(internal.cargoAllocations ?? []),
            ...masterCargoAllocations,
          ];

          // Replace related port calls in scenario
          if (masterAssignment.loadingPortCallId) {
            relatedPortCallCIds.add(masterAssignment.loadingPortCallId);
          }
          if (masterAssignment.dischargingPortCallId) {
            relatedPortCallCIds.add(masterAssignment.dischargingPortCallId);
          }
        }
        internal.portCalls = internal.portCalls
          .filter((pc) => !relatedPortCallCIds.has(pc.clientId))
          .concat(
            masterScenario.portCalls.filter((pc) =>
              relatedPortCallCIds.has(pc.clientId),
            ),
          );

        const masterPatch = masterScenario.patchOrders.get(orderId);
        if (masterPatch) {
          internal.patchOrders.set(orderId, masterPatch);
        } else {
          internal.patchOrders.delete(orderId);
        }

        internal.voyageCharterByOrderId.set(
          orderId,
          masterScenario.voyageCharterByOrderId.get(orderId) ?? null,
        );

        const scenarioAssignment: CargoEntry | undefined =
          getOrderAssignments(internal).get(orderId);
        for (const assignment of [scenarioAssignment, masterAssignment]) {
          if (assignment) {
            internal.entryStates.delete(assignment.clientId);
          }
        }
      }
      break;
    }
    default: {
      if (!internal) {
        console.error(
          `Trying to update scenario ${draft.selectedScenarioCID} but it does not exist.`,
        );
        break;
      }
      singleScenarioReducer(internal, action);
    }
  }

  if (internal) {
    verifyScenarioIntegrity(internal);
  }

  // Committed (Master) always first, then user scenarios in order of creation
  const sortedScenarios = orderBy(draft.scenarios, (s) =>
    isMasterScenario(s) ? '' : (s.createdAt ?? '0'),
  );

  if (
    draft.scenarios.length !== sortedScenarios.length ||
    sortedScenarios.some((n, i) => n.clientId !== draft.scenarios[i].clientId)
  ) {
    draft.scenarios = sortedScenarios;
  }

  // Automatically select a newly added user scenario
  const userScenarioCountNow = draft.scenarios.filter(
    (s) => !isMasterScenario(s),
  ).length;
  if (userScenarioCountBefore === 0 && userScenarioCountNow > 0) {
    draft.selectedScenarioCID =
      draft.scenarios.find(isUserScenario)?.clientId ?? null;
  }

  // If selected scenario no longer exists, select the last valid scenario
  if (
    !findByClientId(
      draft.scenarios.filter((s) => !s.deletedAt),
      draft.selectedScenarioCID,
    )
  ) {
    draft.selectedScenarioCID =
      findLast(
        draft.scenarios.filter((s) => !s.deletedAt),
        (s) => !isBackupScenario(s),
      )?.clientId ?? null;
  }

  const selected = findByClientId(draft.scenarios, draft.selectedScenarioCID);
  draft.isIntegrationScenarioSelected = selected?.isIntegrationMaster === true;
};

export const updateScenariosFromCommitInternal = (
  scenarios: InternalScenarioSchedule[],
  action: ScenarioAction & { type: 'UPDATE_SCENARIOS_FROM_COMMIT' },
): InternalScenarioSchedule[] => {
  const mergeParam: MasterSchedule = {
    ...action.masterScenario,
    dataSchemaVersion: SCENARIO_DATA_SCHEMA_VERSION,
    patchOrders: new Map(),
    voyageCharterByOrderId: new Map(),
    patchShipments: new Map(),
  };
  return scenarios.map((scenario) => {
    if (isUserScenario(scenario)) {
      // Full merge
      if (isAutoSyncEnabledForScenario(action.updateUserScenarios, scenario)) {
        scenario = getMergedScenario(
          scenario,
          mergeParam,
          MERGE_SOURCE_IS_COMMIT,
        );
      } else {
        // Only update the flag to note that we've observed master updating
        scenario = {
          ...scenario,
          masterCommitAt: action.masterScenario.masterCommitAt,
        };
      }
      if (action.updateScenarioPortCallTimes) {
        // Only update port call override timestamps (when using auto-accept)
        const sourcePortCallsById = mapKeyBy(
          mergeParam.portCalls,
          (p) => p.clientId,
        );

        const newPortCalls = scenario.portCalls.map((pc) => {
          const sourcePc = sourcePortCallsById.get(pc.clientId);

          if (!sourcePc || (sourcePc.updatedAt ?? 0) <= (pc.updatedAt ?? 0)) {
            return pc;
          }

          const shouldUpdateArrival =
            sourcePc.plannedArrivalOverride !== null &&
            sourcePc.plannedArrivalOverrideSourcedFrom !== null;

          const shouldUpdateBerthing =
            sourcePc.plannedBerthingOverride !== null &&
            sourcePc.plannedBerthingOverrideSourcedFrom !== null;

          const shouldUpdateDeparture =
            sourcePc.plannedDepartureOverride !== null &&
            sourcePc.plannedDepartureOverrideSourcedFrom !== null;

          return Object.assign({}, pc, {
            ...(shouldUpdateArrival
              ? pick(sourcePc, [
                  'plannedArrivalOverride',
                  'plannedArrivalOverrideSourcedAt',
                  'plannedArrivalOverrideSourcedFrom',
                ])
              : {}),
            ...(shouldUpdateBerthing
              ? pick(sourcePc, [
                  'plannedBerthingOverride',
                  'plannedBerthingOverrideSourcedAt',
                  'plannedBerthingOverrideSourcedFrom',
                ])
              : {}),
            ...(shouldUpdateDeparture
              ? pick(sourcePc, [
                  'plannedDepartureOverride',
                  'plannedDepartureOverrideSourcedAt',
                  'plannedDepartureOverrideSourcedFrom',
                ])
              : {}),
          });
        });

        scenario = {
          ...scenario,
          portCalls: newPortCalls,
        };
      }
    } else if (isMasterScenario(scenario)) {
      scenario = getMergedScenario(
        scenario,
        {
          ...scenario,
          ...action.masterScenario,
        },
        MERGE_SOURCE_IS_COMMIT,
      );
      scenario = pruneUnusedPortCalls(scenario);
    }

    return scenario;
  });
};

const actualScenarioReducer = produceWithPatches(scenarioReducer);

type OrderShipment =
  | PersistedShipment
  | (ScenarioShipment & {
      id: null;
    });

const NO_PATCH = { shipmentId: undefined } as const;

const NO_SHIPMENT: OrderShipment = {
  id: null,
  clientId: `NO-SHIPMENT-invalid-unused-uuid-${generateClientId()}` as ClientId,
  name: 'NONAME-SHIPMENT',
  remarks: null,
  externalShipmentId: null,
  externalShipmentStatus: null,
  voyageAttributeId: null,
  shipmentFreightByShipmentId: null,
  shipmentExtraFreightsByShipmentId: null,
};

function getOrderShipment(
  patch: ScenarioOrderPatch['patch'] | typeof NO_PATCH,
  order: ScenarioTimeline_OrderFragment,
  shipmentByDbId: Map<number, PersistedShipment>,
  /** Expected to contain an entry for every shipment's ClientId, new or old */
  shipmentByClientId: Map<ClientId, ScenarioShipment>,
): OrderShipment {
  let clientId = patch?.shipmentId;
  if (clientId === undefined && isDefined(order.shipmentId)) {
    // No patch available but the order relates to a persisted shipment -> get the clientId from that
    clientId = shipmentByDbId.get(order.shipmentId)?.clientId;
    // This is an unexpected situation. All persisted shipments should be available.
    // SEABER-5025 Maybe this happens when an order is updated via websocket and the shipment is not.
    if (!clientId) {
      console.error(
        `Could not find clientId for shipment ${order.shipmentId} related to order ${order.id}`,
      );
    }
  }

  if (!clientId) {
    // Set to null in scenario patch, or no patch and no persisted shipment
    return NO_SHIPMENT;
  } else {
    // We have a clientId so the shipment exists in the mapping
    const shipment = shipmentByClientId.get(clientId);
    assertDefined(
      shipment,
      `expected all referred shipments to exist in scenarioShipments`,
    );
    return shipment;
  }
}

export type ScenarioOrderWithShipmentId = ScenarioOrder & {
  shipmentId: ClientId | null;
};
/** Object made const to enable memoization with weak keys */
const APPLY_PATCH_OPT_IGNORE_INTEGRATION_CARGO_MARGINS = {
  ignoreIntegrationCargoMargins: true,
};
/** Object made const to enable memoization with weak keys */
const APPLY_PATCH_OPT_NORMAL_MARGINS = {
  ignoreIntegrationCargoMargins: false,
};

const applyPatch = memoize5(function applyPatch(
  order: ScenarioTimeline_OrderFragment,
  orderShipment: ScenarioShipment,
  cargos: CargoAllocation[],
  patch: ScenarioOrderPatch['patch'] | typeof NO_PATCH,
  opts: { ignoreIntegrationCargoMargins: boolean },
): ScenarioOrderWithShipmentId {
  const shipment = orderShipment !== NO_SHIPMENT ? orderShipment : null;
  const ignoreMargins =
    opts.ignoreIntegrationCargoMargins && !!order.externalOrderId;
  const {
    id,
    archived,
    coaId,
    assortmentByAssortmentId,
    assortmentId,
    cancelling,
    cargoAmount,
    cargoUnitByCargoUnitId,
    scheduledOrdersByOrderId,
    outgoingVoyageCharterRelation,
    incomingVoyageCharterRelation,
    chartererOrganizationId,
    orderPortCallRelationsByOrderId,
    orderDetailsByOrderId,
    externalOrderId,
    orderAttributeAssociationsByOrderId,
    orderRequirementAssociationsByOrderId,
    createdAt,
    dischargePortId,
    locationByDischargePortId,
    locationByLoadPortId,
    handledBySpotByOrderId,
    dischargingUnit,
    dischargingRate,
    loadPortId,
    loadingRate,
    loadingUnit,
    marginUnit,
    marginAmount,
    completed,
    rta,
    deliveredBefore,
    layday,
    nominatedVessel,
    specificGravity,
    started,
    stowageFactorMax,
    stowageFactorMin,
    loadingOverheadDuration,
    dischargingOverheadDuration,
    organizationByChartererOrganizationId,
    apiOrdersNoticesByOrderId,
    userByUserId,
    __typename,
    toBeDeleted,
    minVesselClassRequirement,
    vesselAttributeByMinVesselClassRequirement,
    cargoExclusionsByOrderId,
  } = order;
  return {
    id,
    archived,
    coaId,
    assortmentByAssortmentId,
    assortmentId,
    cancelling,
    cargoAmount,
    cargoUnitByCargoUnitId,
    scheduledOrdersByOrderId,
    outgoingVoyageCharterRelation,
    incomingVoyageCharterRelation,
    chartererOrganizationId,
    orderPortCallRelationsByOrderId,
    orderDetailsByOrderId,
    externalOrderId,
    orderAttributeAssociationsByOrderId,
    orderRequirementAssociationsByOrderId,
    createdAt,
    dischargePortId,
    locationByDischargePortId: locationByDischargePortId && {
      ...locationByDischargePortId,
      workingHours:
        locationByDischargePortId.workingHours &&
        workingHoursSchema.parse(locationByDischargePortId.workingHours),
    },
    locationByLoadPortId: locationByLoadPortId && {
      ...locationByLoadPortId,
      workingHours:
        locationByLoadPortId.workingHours &&
        workingHoursSchema.parse(locationByLoadPortId.workingHours),
    },
    handledBySpotByOrderId,
    dischargingUnit,
    dischargingRate,
    loadPortId,
    loadingRate,
    loadingUnit,
    marginUnit,
    marginAmount: ignoreMargins ? 0 : marginAmount,
    completed,
    rta,
    deliveredBefore,
    layday,
    loadingOverheadDuration,
    dischargingOverheadDuration,
    loadingWindow: {
      start: dateStringToNumOpt(layday) ?? undefined,
      end: dateStringToNumOpt(cancelling) ?? undefined,
    },
    dischargingWindow: {
      start: dateStringToNumOpt(rta) ?? undefined,
      end: dateStringToNumOpt(deliveredBefore) ?? undefined,
    },
    cargoRanges: calculateMassAndVolumeRanges(order, ignoreMargins),
    nominatedVessel,
    specificGravity,
    started,
    stowageFactorMax,
    stowageFactorMin,
    __typename,
    ...patch,
    shipmentId: shipment?.clientId ?? null,
    shipment,
    cargoAllocations: cargos,
    organizationByChartererOrganizationId,
    apiOrdersNoticesByOrderId,
    userByUserId,
    toBeDeleted,
    minVesselClassRequirement,
    vesselAttributeByMinVesselClassRequirement,
    cargoExclusionsByOrderId,
  };
});

const EmptyArray: [] = [];

export const getShipmentsById = memoize(function getShipmentsById(
  shipmentByClientId: Map<ClientId, PersistedShipment>,
) {
  return mapKeyBy([...shipmentByClientId.values()], (shipment) => shipment.id);
});

const buildShipmentPatch = (): PatchShipment => ({
  voyageAttributeId: null,
});

export const buildShipment = (clientId: ClientId): ScenarioShipment => ({
  id: null,
  clientId,
  name: null,
  remarks: null,
  externalShipmentId: null,
  externalShipmentStatus: null,
  voyageAttributeId: null,
  shipmentFreightByShipmentId: null,
  shipmentExtraFreightsByShipmentId: null,
});

const getShipmentsByClientId = memoize4(function getShipmentsByClientId(
  allShipmentClientIds: Set<ClientId>,
  persistedShipmentByClientId: Map<ClientId, PersistedShipment>,
  patchByShipmentId: UserSchedule['patchShipments'],
  externalShipmentIdByClientId: Record<string, string>,
) {
  return new Map<ClientId, ScenarioShipment>(
    [...allShipmentClientIds].map((clientId) => {
      const patch = patchByShipmentId.get(clientId);
      const persisted = persistedShipmentByClientId.get(clientId);
      const shipment: ScenarioShipment = {
        ...(persisted ?? buildShipment(clientId)),
        ...patch,
        voyageAttributeId:
          persisted?.voyageAttributeId ?? patch?.voyageAttributeId ?? null,
      };

      const externalShipmentId = externalShipmentIdByClientId?.[clientId];
      if (externalShipmentId) {
        shipment.externalShipmentId = externalShipmentId;
      }
      return [clientId, shipment];
    }),
  );
});

const getCargoAllocationsByOrderId = memoize(
  function getCargoAllocationsByOrderId(cargoAllocations: CargoAllocation[]) {
    return mapGroupBy(cargoAllocations, (c) => c.orderId);
  },
);

export const getScenarioOrdersAndShipments = (
  orderById: Map<number, ScenarioTimeline_OrderFragment>,
  persistedShipmentByClientId: Map<ClientId, PersistedShipment>,
  scenario: Pick<
    UserSchedule,
    'patchOrders' | 'patchShipments' | 'cargoAllocations' | 'integrationData'
  > | null,
  opts: { ignoreIntegrationCargoMargins: boolean },
): {
  scenarioOrderById: Map<OrderId, ScenarioOrderWithShipmentId>;
  scenarioOrders: ScenarioOrderWithShipmentId[];
  scenarioShipmentByClientId: Map<ClientId, ScenarioShipment>;
} => {
  const { patchOrders, patchShipments, cargoAllocations, integrationData } =
    scenario ?? {};

  const externalShipmentIdByClientId =
    integrationData?.externalShipmentIdByClientId ?? {};

  const shipmentByDbId = getShipmentsById(persistedShipmentByClientId);

  const allShipmentClientIds = new Set<ClientId>();

  for (const key of persistedShipmentByClientId.keys()) {
    allShipmentClientIds.add(key);
  }
  if (patchShipments) {
    for (const key of patchShipments.keys()) {
      allShipmentClientIds.add(key);
    }
  }
  if (patchOrders) {
    for (const patch of patchOrders.values()) {
      if (patch.patch.shipmentId) {
        allShipmentClientIds.add(patch.patch.shipmentId);
      }
    }
  }

  const scenarioShipmentByClientId = getShipmentsByClientId(
    allShipmentClientIds,
    persistedShipmentByClientId,
    patchShipments ?? new Map(),
    externalShipmentIdByClientId,
  );

  const persistedCargoAllocations: CargoAllocation[] = [
    ...orderById.values(),
  ].flatMap((order) =>
    compact(order.cargoCompartmentAllocationsByOrderId.nodes).map(
      CargoAllocationFragmentToEntry,
    ),
  );

  const cargoAllocationsByOrderId = getCargoAllocationsByOrderId(
    cargoAllocations ?? persistedCargoAllocations,
  );

  const scenarioOrderById = new Map<OrderId, ScenarioOrderWithShipmentId>();

  const scenarioOrders: ScenarioOrderWithShipmentId[] = [];

  for (const order of orderById.values()) {
    const orderId = order.id;
    const patch = patchOrders?.get(orderId);
    switch (patch?.type) {
      case undefined:
      case 'update':
        const cargos = cargoAllocationsByOrderId.get(orderId) ?? EmptyArray;
        const orderPatch = patch?.patch ?? NO_PATCH;
        const shipment = getOrderShipment(
          orderPatch,
          order,
          shipmentByDbId,
          scenarioShipmentByClientId,
        );
        const value = applyPatch(
          order,
          shipment,
          cargos,
          orderPatch,
          opts.ignoreIntegrationCargoMargins
            ? APPLY_PATCH_OPT_IGNORE_INTEGRATION_CARGO_MARGINS
            : APPLY_PATCH_OPT_NORMAL_MARGINS,
        );
        scenarioOrderById.set(orderId, value);
        scenarioOrders.push(value);
        break;
      default:
        // Not using other types right now, TODO: update if needed
        throw new Error(`Patch type not currently supported: ${patch?.type}`);
    }
  }

  return {
    scenarioOrderById,
    scenarioOrders,
    scenarioShipmentByClientId,
  };
};

export enum ScenarioCommitPolicy {
  /** All scenario assignments are committed, conflicting ones and anything not present in scenario gets removed. */
  OVERRIDE = 'override',
  /** Preserves data in db that is not in conflict with the scenario, overwrites everything else with the scenario data. */
  MERGE_COMPATIBLE = 'merge',
}

export type UseScenariosOpts = {
  autoUpdateUserScenarios: boolean /** Auto update scenarios with new committed data from websocket. When false only committed (master) scenario is updated. */;
  masterCommitAt:
    | number
    | null /** Version number of the last known commit in current org. */;
};

export type CommitStatus = {
  currentActionName: string | null;
  currentCompleted?: number;
  currentTotal?: number;
};

/** Returns a new scenario. Several scenario fields are required to be provided, applies several (overridable) default values to others.  */
export const initScenario = (
  params: Pick<
    InternalUserSchedule,
    | 'createdBy'
    | 'organizationId'
    | 'name'
    | 'schedule'
    | 'spotAssignments'
    | 'coaAssignments'
    | 'portCalls'
  > &
    Partial<InternalUserSchedule>,
): InternalUserSchedule => {
  const now = new Date();
  return {
    type: 'user',
    clientId: generateClientId(),
    updatedBy: params.createdBy,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    dataSchemaVersion: SCENARIO_DATA_SCHEMA_VERSION,
    masterCommitAt: null,
    migrationVersion: LATEST_MIGRATION_VERSION,
    isReadOnly: false,
    isIntegrationMaster: false,
    autoUpdateFromMaster: 'default',
    id: null,
    name: generateScenarioName([], params.initials ?? ''),
    description: null,
    patchOrders: new Map(),
    voyageCharterByOrderId: new Map(),
    entryStates: new Map(),
    shared: false,
    cargoAllocations: [],
    patchShipments: new Map(),
    orderShortlist: { orderIds: [], updatedAt: 0 },
    vesselsShortlist: { vesselIds: [], updatedAt: 0 },
    freightByShipmentClientId: new Map(),
    extraFreightByShipmentClientId: new Map(),
    ...params,
  };
};

type CommitScenarioFn = (
  scenarioCID: ClientId,
  timelines: EventTimeline[],
  getScenarioData: GetScenarioData,
  opts: {
    policy: ScenarioCommitPolicy;
    statusUpdate?: (status: CommitStatus) => void;
    suppressEmail: boolean;
    committedData: CommittedPlanningData;
    mergeDirectives: MergeDirectives;
  },
) => Promise<void>;

type UndoTransaction = {
  id: ClientId;
  /** Always set when manually created, always undefined when automatic by an action not manually wrapped in a tx */
  title?: string;
  actions: ScenarioAction['type'][];
  /** When set, it's expected that we prompt the user with this confirmation text before undo */
  // undoConfirm?: string;
  undoPatches: Patch[];
  redoPatches: Patch[];
};
export type TransactionParams = Pick<
  MakeOptional<UndoTransaction, 'id'>,
  'id' | 'title'
> & { title: string };

// This one should only be used to return a valid scenario when none exists in the reducer store. Better yet, could just return a null but this is used instead in favor of reducing the number of required changes for now.
export const dummyScenario: InternalInitialSchedule = {
  initials: '',
  cargoAllocations: [],
  changedAt: 0,
  clientId: generateClientId(),
  coaAssignments: [],
  createdAt: undefined,
  createdBy: 0 as UserId,
  dataSchemaVersion: SCENARIO_DATA_SCHEMA_VERSION,
  masterCommitAt: null,
  deletedAt: undefined,
  description: undefined,
  isReadOnly: true,
  isIntegrationMaster: false,
  autoUpdateFromMaster: 'default',
  name: undefined,
  organizationId: 0 as OrganizationId,
  patchOrders: new Map(),
  portCalls: [],
  schedule: [],
  shared: false,
  spotAssignments: [],
  type: 'initial',
  updatedAt: undefined,
  updatedBy: 0 as UserId,
  voyageCharterByOrderId: new Map(),
  patchShipments: new Map(),
  freightByShipmentClientId: new Map(),
  extraFreightByShipmentClientId: new Map(),
};

type UndoActionType = ScenarioAction['type'] &
  (
    | 'BEGIN_UNDO_TRANSACTION'
    | 'END_UNDO_TRANSACTION'
    | 'UNDO_STEP'
    | 'REDO_STEP'
    | 'CLEAR_UNDO_STACK'
    | 'ROLLBACK_UNDO_TRANSACTION'
  );

const isUndoAction = (
  action: ScenarioAction,
): action is ScenarioAction & {
  type: UndoActionType;
} => {
  return [
    'BEGIN_UNDO_TRANSACTION',
    'END_UNDO_TRANSACTION',
    'UNDO_STEP',
    'REDO_STEP',
    'CLEAR_UNDO_STACK',
    'ROLLBACK_UNDO_TRANSACTION',
  ].includes(action.type);
};

export const MAX_UNDO_STACK = 100;

export type CreateAndUpdateAssignments = (
  changed: ChangedEntries,
  estimateIdOrScenario: ClientId | 'scenario',
) => void;

export type UseScenariosResult = {
  loading: boolean;
  createAssignment: (
    scheduleEntry: Entry,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  selectScenario: (scenarioCID: ScenarioSchedule['clientId']) => void;
  scenarioShipmentByClientId: Map<ClientId, ScenarioShipment>;
  scenarioOrderById: Map<OrderId, ScenarioOrder>;
  scenarioOrders: ScenarioOrder[];

  undo: {
    /** Returns the transactionId. Always call endTransaction when done. */
    beginTransaction: (params: TransactionParams) => ClientId;
    /* If clientId given, we'll use it to verify that we're ending the correct transaction. This helps us not terminate a transaction early with a nested "begin" within. */
    endTransaction: (clientId?: ClientId) => void;
    transactionWrapper: <T extends [], U>(
      params: TransactionParams,
    ) => (fn: (...args: T) => U) => (...args: T) => U;
    withTransaction: <T>(
      params: TransactionParams & { noUndoOnError?: boolean },
      fn: () => T,
    ) => T;
    undo: () => void;
    redo: () => void;
    clearUndoStack: () => void;
    canUndo: boolean;
    canRedo: boolean;
    undoHint: string;
    redoHint: string;
  };

  setScenarios: (
    scenarios:
      | ScenarioSchedule[]
      | ((oldScenarios: ScenarioSchedule[]) => ScenarioSchedule[]),
  ) => void;
  mergeScenario: (
    sourceCID: ScenarioSchedule['clientId'],
    targetCID: ScenarioSchedule['clientId'],
  ) => void;
  updateScenarios: (
    scenarios: InternalScenarioSchedule[],
    source?: ScenarioUpdateSource,
  ) => void;
  updateScenariosOnReconnect: (
    scenarios: (Scenarios_ScenarioFragment | null)[],
  ) => void;
  selectedScenario: ScenarioSchedule;
  createAndUpdateAssignments: CreateAndUpdateAssignments;
  createEstimateTimeline: (
    vesselId: number,
    newEstimateId: ClientId | undefined,
    oldEstimateIdOrScenario: ClientId | 'scenario',
    vesselReservations?: EstimateVesselReservation[],
    openPositions?: EstimateOpenPosition[],
  ) => void;
  applyEstimateTimeline: (
    estimateId: ClientId,
    keepEstimates: boolean,
    currentVesselReservations: VesselReservationFragment[],
    currentOpenPositions: OpenPositionsFragment[],
  ) => void;
  deleteEstimateTimeline: (estimateId: ClientId) => void;
  addReservationToEstimate: (
    estimateId: ClientId,
    vesselReservation: VesselReservationFragment,
  ) => void;
  addOpenPositionToEstimate: (
    estimateId: ClientId,
    openPosition: OpenPositionsFragment,
  ) => void;
  removeReservationFromEstimate: (
    estimateId: ClientId,
    vesselReservationId: number,
  ) => void;
  removeOpenPositionFromEstimate: (
    estimateId: ClientId,
    openPositionId: number,
  ) => void;
  scenarios: ScenarioSchedule[];
  deleteScenarioToTrash: (scenarioCID: ScenarioSchedule['clientId']) => void;
  restoreIntegrationScenario: () => void;
  deleteScenarioCompletely: (scenarioCID: ScenarioSchedule['clientId']) => void;
  restoreScenario: (scenarioCID: ScenarioSchedule['clientId']) => void;
  deleteAssignment: (
    orderId: Order['id'],
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  deleteEvent: (
    eventId: ClientId,
    eventCategory: EventCategory.Single | EventCategory.OffHire,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  createScenario: (
    scenario?: ScenarioSchedule,
    opts?: { keepName: boolean; selectCreated: boolean },
  ) => void;
  createEmptyScenario: () => void;
  saveScenario: (
    scenarioCID: ScenarioSchedule['clientId'],
    opts: {
      /** When given, updates the scenario shared value. `undefined` causes no update on the prop. */
      share?: boolean;
      /** When given, use this data instead of whatever exists in scenario store by the scenarioCID*/
      data?: UserSchedule;
      /** When true, save only happens if the scenario already exists in DB */
      updateOnly?: boolean;
    },
  ) => Promise<InternalScenarioSchedule | undefined>;
  renameScenario: (clientId: ClientId, scenarioName: string) => void;
  commitScenario: CommitScenarioFn;
  /** Index in the scenarios array. May not be equal to the position of the scenario in the UI. */
  selectedScenarioCID: ClientId | null;
  updateAssignmentsSharing: (
    ids: Set<number>,
    shared: boolean,
  ) => Promise<void>;
  resetAssignmentsToMaster: (
    orderIds: number[],
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  setOrderVoyageCharter: (
    orderId: number,
    voyageCharterId: number | string | null,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  setVoyageCharterForOrders: (
    orders: ScenarioOrder[],
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  updateOrder: (
    orderId: number,
    patch: ScenarioOrderPatch['patch'],
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  updateScenariosAfterArchive: () => Promise<void>;
  saveAllocations: (
    allocations: CargoAllocation[],
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  patchShipment: (
    clientId: ClientId,
    patch: PatchShipment,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  setAutoSync: (
    newValue: InternalScenarioSchedule['autoUpdateFromMaster'],
  ) => void;
  toggleShortlistOrders: (orderIds: number[], shortlisted: boolean) => void;
  setShortlist: (orderIds: number[]) => void;
  toggleShortlistVessels: (vesselId: number) => void;
  setShortlistVessels: (vesselIds: number[]) => void;
  setEstimateFreightOverrides: (
    estimateId: ClientId,
    overridesByOrderId: FreightRateOverridesByOrderId,
  ) => void;
  setEstimateShipmentFreightOverrides: (
    estimateId: ClientId,
    overridesByShipmentId: ShipmentFreightOverridesByShipmentId,
  ) => void;
  setShipmentFreight: (
    shipmentClientId: ClientId,
    freight: { freightAmount: number; freightUnit: PaymentUnit } | null,
  ) => void;
  setShipmentExtraFreights: (
    shipmentClientId: ClientId,
    newEntries: ScenarioShipmentExtraFreight[],
  ) => void;
};

export const useScenarios = (
  handledBySpots: ReadonlyArray<ScenarioTimeline_HandledBySpotFragment>,
  orderById: Map<number, ScenarioTimeline_OrderFragment>,
  withoutCompletedSchedules: TCScheduleEntry[],
  allSchedules: TCScheduleEntry[],
  coaAssignments: ReadonlyArray<ScenarioTimeline_CoaAssignmentFragment>,
  portCallsById: Map<number, ScenarioTimeline_PortCallFragment>,
  persistedShipmentByClientId: Map<ClientId, PersistedShipment>,
  dataLoading: boolean,
  skipLocalScenarios = false,
  opts: UseScenariosOpts,
  isCommitRunning: MutableRefObject<boolean>,
  onFinishCommit: () => void,
  completedOrderIds: Set<number>,
  completedOffHireIds: Set<number>,
  completedEventIds: Set<number>,
  onlyFetchCommittedData = false,
): UseScenariosResult => {
  const client = useApolloClient();
  const currentUser = useCurrentUser();
  const { setIsCommitRunning } = useContext(AppStateContext);
  const currentUserId = currentUser?.id ?? null;
  const initials = currentUser?.initials ?? '';

  const currentOrgId = currentUser?.organizationId ?? null;
  const [userLocallyDeletedScenarios, setUserLocallyDeletedScenarios] = useAtom(
    userLocallyDeletedScenariosAtomFamily(currentUserId),
  );
  const isAutoAcceptUpdatesEnabled =
    currentUser?.rootOrganization?.configAutoAcceptUpdates &&
    !currentUser.organization?.configDisablePcUpdates;
  const ignoreIntegrationCargoMargins =
    !!currentUser?.organization?.configIgnoreIntegrationCargoMargins;
  const { masterCommitAt } = opts;
  const { data: savedScenariosData, loading: savedScenariosLoading } =
    useScenariosQuery({
      variables: {
        scenarioDataSchemaVersion: SCENARIO_DATA_SCHEMA_VERSION,
      },
      fetchPolicy: 'no-cache',
      skip: onlyFetchCommittedData,
    });
  const { data: archivedData, loading: archivedDataLoading } =
    useArchivedDataQuery({
      fetchPolicy: 'no-cache',
      skip: onlyFetchCommittedData,
    });

  const [savedScenariosInitialized, setSavedScenariosInitialized] =
    useState(false);

  const localSaveChecked = useRef(false);

  const [scenarioCIDSavedStr, setScenarioCIDSaved] = useLocalSetting(
    'scenarioCIDSaved',
    null,
  );

  const scenarioCIDSaved =
    scenarioCIDSavedStr === null ? null : parseClientId(scenarioCIDSavedStr);

  const [
    {
      scenarios: reducerScenarios,
      selectedScenarioCID,
      isIntegrationScenarioSelected,
      undoStack,
      redoStack,
    },
    dispatch,
  ] = useReducer(
    (state: InternalScenarioState, action: ScenarioAction) => {
      let nextState: InternalScenarioState;
      if (isUndoAction(action)) {
        const lastTx = state.undoStack.at(-1);
        switch (action.type) {
          case 'BEGIN_UNDO_TRANSACTION': {
            // Ensuring we don't start nested transactions (which wouldn't work nor be meaningful)
            if (
              state.currentTransaction !== null &&
              lastTx?.title !== undefined
            ) {
              nextState = state;
              break;
            }
            const transaction: UndoTransaction = {
              ...omit(action, 'type'),
              id: action.id ?? generateClientId(),
              undoPatches: [],
              redoPatches: [],
              actions: [],
            };

            // Previous tx already has items, add new one
            nextState = {
              ...state,
              undoStack: [...state.undoStack, transaction],
              currentTransaction: transaction.id,
            };
            break;
          }
          case 'END_UNDO_TRANSACTION': {
            if (action.transactionId && lastTx?.id !== action.transactionId) {
              // This should mean we're trying to end a nested transaction. It wasn't created, so do nothing.
              nextState = state;
              break;
            }
            nextState = {
              ...state,
              // If there were no patches in the transaction we can clean it up
              undoStack: !lastTx?.undoPatches.length
                ? state.undoStack.slice(0, -1)
                : state.undoStack,
              currentTransaction: null,
            };
            break;
          }
          case 'CLEAR_UNDO_STACK': {
            nextState = {
              ...state,
              undoStack: [],
              redoStack: [],
              currentTransaction: null,
            };
            break;
          }
          case 'UNDO_STEP': {
            const undoTx = state.undoStack.at(-1);
            if (!undoTx) {
              console.warn(`No actions to undo`);
              nextState = state;
              break;
            }
            const patchesToApply = undoTx.undoPatches.slice().reverse();
            nextState = {
              ...applyPatches(state, patchesToApply),
              undoStack: state.undoStack.slice(0, -1),
              redoStack: [...state.redoStack, undoTx],
            };
            break;
          }
          case 'ROLLBACK_UNDO_TRANSACTION': {
            const undoTx = state.undoStack.at(-1);
            if (!undoTx || undoTx.id !== action.transactionId) {
              nextState = state;
              break;
            }
            const patchesToApply = undoTx.undoPatches.slice().reverse();
            nextState = {
              ...applyPatches(state, patchesToApply),
              undoStack: state.undoStack.slice(0, -1),
              redoStack: [],
              currentTransaction: null,
            };
            break;
          }
          case 'REDO_STEP': {
            const redoTx = state.redoStack.at(-1);
            if (!redoTx) {
              console.warn(`No actions to redo`);
              nextState = state;
              break;
            }
            const patchesToApply = redoTx.redoPatches;
            nextState = {
              ...applyPatches(state, patchesToApply),
              undoStack: [...state.undoStack, redoTx],
              redoStack: state.redoStack.slice(0, -1),
            };
            break;
          }
          default: {
            nextState = state;
            exhaustiveCheck(action);
          }
        }
      } else {
        const [nextStateFromReducer, patches, inversePatches] =
          actualScenarioReducer(state, action);
        nextState = nextStateFromReducer;

        const nextUndoStack = [...state.undoStack];
        let currentTx = state.undoStack.at(-1);
        if (
          (!currentTx || state.currentTransaction === null) &&
          // Only create an automatic new batch of updates if there's at least one manual transaction first
          // This avoids creating a bunch of page initialization making one long transaction that we could undo
          state.undoStack.at(0)?.title !== undefined
        ) {
          currentTx = {
            id: generateClientId(),
            undoPatches: [],
            redoPatches: [],
            actions: [action.type],
          };
          nextUndoStack.push(currentTx);
        }

        if (currentTx) {
          nextState = {
            ...nextState,
            undoStack: [
              // Limit to max & drop last, which is replaced
              ...nextUndoStack.slice(-MAX_UNDO_STACK, -1),
              {
                ...currentTx,
                undoPatches: [...currentTx.undoPatches, ...inversePatches],
                redoPatches: [...currentTx.redoPatches, ...patches],
                actions: [...currentTx.actions, action.type],
              },
            ],
            currentTransaction: currentTx.id,
          };
        }
      }

      if (action.type !== 'UNDO_STEP' && action.type !== 'REDO_STEP') {
        if (nextState.redoStack.length > 0) {
          // Clear redoStack
          nextState = {
            ...nextState,
            redoStack: [],
          };
        }
      }

      return nextState;
    },
    {
      scenarios: [],
      selectedScenarioCID: scenarioCIDSaved,
      isIntegrationScenarioSelected: false,
      undoStack: [],
      redoStack: [],
      currentTransaction: null,
    },
  );

  // Hide shared scenarios which user has deleted locally
  const scenarios = useMemo(
    () =>
      reducerScenarios.map((s) => {
        const isDeletedLocally = userLocallyDeletedScenarios.includes(
          s.clientId,
        );
        return isDeletedLocally && !s.deletedAt
          ? { ...s, deletedAt: new Date().toISOString() }
          : s;
      }),
    [reducerScenarios, userLocallyDeletedScenarios],
  );

  const schedule = isIntegrationScenarioSelected
    ? allSchedules
    : withoutCompletedSchedules;

  const shipmentByDbId = useMemo(
    () => getShipmentsById(persistedShipmentByClientId),
    [persistedShipmentByClientId],
  );

  const buildScenarioBase: () => Omit<InternalUserSchedule, 'name'> =
    useCallback(() => {
      const cargoAllocations: CargoAllocation[] = [];
      const patchOrders: Map<OrderId, ScenarioOrderPatch> = new Map();
      const assignedOrderIds = new Set([
        ...schedule.flatMap((e) => (isCargoEntry(e) ? [e.orderId] : [])),
        ...handledBySpots.map((e) => e.orderId).filter(isDefined),
        ...coaAssignments.map((e) => e.orderId),
      ]);

      orderById.forEach((order) => {
        order.cargoCompartmentAllocationsByOrderId.nodes.forEach(
          (allocation) => {
            if (isDefined(allocation)) {
              cargoAllocations.push(
                omit(CargoAllocationFragmentToEntry(allocation), 'dbId'),
              );
            }
          },
        );

        // Store shipment info for every assigned order
        if (assignedOrderIds.has(order.id)) {
          patchOrders.set(order.id, {
            id: order.id,
            type: 'update',
            patch: {
              shipmentId:
                shipmentByDbId.get(order.shipmentId ?? (NaN as ShipmentId))
                  ?.clientId ?? null,
            },
          });
        }
      });

      const freightByShipmentClientId = new Map();
      const extraFreightByShipmentClientId = new Map();

      for (const [
        shipmentClientId,
        { shipmentFreightByShipmentId, shipmentExtraFreightsByShipmentId },
      ] of persistedShipmentByClientId) {
        if (shipmentFreightByShipmentId) {
          freightByShipmentClientId.set(
            shipmentClientId,
            shipmentFreightByShipmentId,
          );
        }
        if (shipmentExtraFreightsByShipmentId?.length) {
          extraFreightByShipmentClientId.set(
            shipmentClientId,
            shipmentExtraFreightsByShipmentId,
          );
        }
      }

      return initScenario({
        initials,
        createdBy: assertedDefined(currentUserId, 'currentUserId'),
        organizationId: assertedDefined(currentOrgId, 'currentOrgId'),
        schedule: schedule,
        spotAssignments: handledBySpots.map((fragment) =>
          spotFragmentToEntry(fragment, orderById),
        ),
        coaAssignments: coaAssignments.map((fragment) =>
          coaFragmentToEntry(fragment, orderById),
        ),
        portCalls: [...portCallsById.values()].map(portCallFragmentToEntry),
        cargoAllocations,
        patchOrders,
        syncedWithMasterAt: masterCommitAt ?? Date.now(),
        masterCommitAt,
        patchShipments: new Map(
          [...persistedShipmentByClientId.values()].map(
            (shipment): [ClientId, PatchShipment] => [
              shipment.clientId,
              { voyageAttributeId: shipment.voyageAttributeId },
            ],
          ),
        ),
        freightByShipmentClientId,
        extraFreightByShipmentClientId,
      });
    }, [
      initials,
      schedule,
      handledBySpots,
      coaAssignments,
      orderById,
      currentUserId,
      currentOrgId,
      portCallsById,
      masterCommitAt,
      shipmentByDbId,
      persistedShipmentByClientId,
    ]);

  const buildDefaultScenario: () => InternalUserSchedule = useCallback(() => {
    return {
      ...buildScenarioBase(),
      name: generateScenarioName(scenarios, initials),
    };
  }, [buildScenarioBase, scenarios, initials]);

  const buildEmptyScenario: () => InternalUserSchedule = useCallback(() => {
    return initScenario({
      initials,
      createdBy: assertedDefined(currentUserId, 'currentUserId'),
      organizationId: assertedDefined(currentOrgId, 'currentOrgId'),
      name: generateScenarioName(scenarios, initials),
      schedule: [],
      spotAssignments: [],
      coaAssignments: [],
      portCalls: [],
      syncedWithMasterAt: masterCommitAt ?? Date.now(),
      masterCommitAt,
    });
  }, [currentUserId, currentOrgId, scenarios, masterCommitAt, initials]);

  // Master => committed
  const buildMasterScenario: () => InternalMasterSchedule = useCallback(() => {
    return {
      ...buildScenarioBase(),
      type: 'master',
      name: 'Main',
      isReadOnly: true,
      clientId: MASTER_SCENARIO_CLIENTID,
    };
  }, [buildScenarioBase]);

  const selectedScenario = !dataLoading
    ? (findByClientId(scenarios, selectedScenarioCID) ?? dummyScenario)
    : dummyScenario;

  const notCompletedAssignments = useMemo(() => {
    return selectedScenario.isIntegrationMaster
      ? null
      : {
          schedule: selectedScenario.schedule.filter(
            (e) =>
              !(isTCOrderEntry(e) && completedOrderIds.has(e.orderId)) &&
              !(isSingleEntry(e) && e.dbId && completedEventIds.has(e.dbId)) &&
              !(isOffHireEntry(e) && e.dbId && completedOffHireIds.has(e.dbId)),
          ),
          cargoAllocations: selectedScenario.cargoAllocations.filter(
            (e) => !completedOrderIds.has(e.orderId),
          ),
          spotAssignments: selectedScenario.spotAssignments.filter(
            (e) => !completedOrderIds.has(e.orderId),
          ),
          coaAssignments: selectedScenario.coaAssignments.filter(
            (e) => !completedOrderIds.has(e.orderId),
          ),
        };
  }, [
    selectedScenario.isIntegrationMaster,
    selectedScenario.schedule,
    selectedScenario.cargoAllocations,
    selectedScenario.spotAssignments,
    selectedScenario.coaAssignments,
    completedOrderIds,
    completedEventIds,
    completedOffHireIds,
  ]);

  const scenario =
    selectedScenario === dummyScenario
      ? dummyScenario
      : { ...selectedScenario, ...notCompletedAssignments };

  const [localSaveLoadCompleted, setLocalSaveLoadCompleted] =
    useState<boolean>(false);

  const scenarioForOrdersAndShipments = useMemo(() => {
    return {
      patchOrders: selectedScenario.patchOrders,
      patchShipments: selectedScenario.patchShipments,
      cargoAllocations: selectedScenario.cargoAllocations,
      integrationData: selectedScenario.integrationData,
    };
  }, [
    selectedScenario.cargoAllocations,
    selectedScenario.integrationData,
    selectedScenario.patchOrders,
    selectedScenario.patchShipments,
  ]);
  const { scenarioOrderById, scenarioOrders, scenarioShipmentByClientId } =
    useMemo(
      () =>
        getScenarioOrdersAndShipments(
          orderById,
          persistedShipmentByClientId,
          scenarioForOrdersAndShipments,
          { ignoreIntegrationCargoMargins },
        ),
      [
        ignoreIntegrationCargoMargins,
        orderById,
        persistedShipmentByClientId,
        scenarioForOrdersAndShipments,
      ],
    );

  useEffect(() => {
    const saveScenariosLocally = async () => {
      if (localSaveLoadCompleted) {
        await writeStore(
          'scenarios',
          scenarios.filter(
            (s) =>
              !isMasterScenario(s) &&
              !isSuggestionScenario(s) &&
              s.isIntegrationMaster !== true,
          ),
        );
      }
    };
    void saveScenariosLocally();
  }, [scenarios, localSaveLoadCompleted]);

  const voyageNamingRuleByShipId = useVoyageNamingRules(currentOrgId);

  const selectScenario = useCallback(
    (scenarioCID: ScenarioSchedule['clientId'] | null) => {
      const scenarioToSelect = findByClientId(scenarios, scenarioCID);
      if (scenarioToSelect && isBackupScenario(scenarioToSelect)) {
        console.error(`Cannot select backup scenarios`, {
          scenarioCID,
          numScenarios: scenarios.length,
        });
        return;
      }

      if (typeof window !== 'undefined') {
        setScenarioCIDSaved(scenarioCID);
      }

      dispatch({
        type: 'SELECT_SCENARIO',
        scenarioCID,
      });

      dispatch({
        type: 'CLEAR_UNDO_STACK',
      });

      clearTimeDataCache();
      client.cache.gc();

      if (
        typeof window !== 'undefined' &&
        process.env.NODE_ENV === 'development'
      ) {
        import('../lib/memoryMonitor').then(({ logMemorySnapshot }) => {
          logMemorySnapshot('Scenario switch');
        });
      }
    },
    [scenarios, setScenarioCIDSaved, client],
  );

  const beginUndoTransaction = useCallback(
    (params: MakeOptional<TransactionParams, 'id'>): ClientId => {
      const transactionId = params.id ?? generateClientId();
      dispatch({
        type: 'BEGIN_UNDO_TRANSACTION',
        ...params,
        id: transactionId,
      });
      return transactionId;
    },
    [],
  );
  const endUndoTransaction = useCallback((transactionId?: ClientId): void => {
    dispatch({
      type: 'END_UNDO_TRANSACTION',
      transactionId,
    });
  }, []);

  const withTransaction = useCallback(
    <T>(
      params: TransactionParams & { noUndoOnError?: boolean },
      fn: () => T,
    ): T => {
      const transactionId = params.id ?? generateClientId();
      dispatch({
        type: 'BEGIN_UNDO_TRANSACTION',
        ...params,
        id: transactionId,
      });

      let resultOrPromise: T | Error;

      try {
        resultOrPromise = fn();
      } catch (err) {
        resultOrPromise = err;
      }

      // Note: the type 'T' is not exactly correct here when T is a Promise<T>, but was unable to find a way to make this all type check properly
      const onResult = (result: T) => {
        dispatch({
          type: 'END_UNDO_TRANSACTION',
          transactionId,
        });
        return result;
      };

      const onError = (error: Error) => {
        if (!params.noUndoOnError) {
          // If the handler within transaction failed, undo all its changes
          dispatch({
            type: 'ROLLBACK_UNDO_TRANSACTION',
            transactionId,
          });
        }
        throw error;
      };

      if (resultOrPromise instanceof Promise) {
        return resultOrPromise.then(onResult).catch(onError) as T;
      } else {
        if (resultOrPromise instanceof Error) {
          return onError(resultOrPromise);
        } else {
          return onResult(resultOrPromise);
        }
      }
    },
    [],
  );

  const transactionWrapper = useCallback(
    <T extends unknown[], U>(params: TransactionParams) =>
      (fn: (...args: T) => U) => {
        return (...args: T) => withTransaction(params, () => fn(...args));
      },
    [withTransaction],
  );

  const undoFn = useEvent((): void => {
    toast.info(`Undo ${undo.undoHint}`);
    dispatch({
      type: 'UNDO_STEP',
    });
  });
  const redoFn = useEvent((): void => {
    toast.info(`Redo ${undo.redoHint}`);
    dispatch({
      type: 'REDO_STEP',
    });
  });
  const clearUndoStack = useCallback((): void => {
    dispatch({
      type: 'CLEAR_UNDO_STACK',
    });
  }, []);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;
  const createUndoHint = (stack: UndoTransaction[]): string => {
    const lastTx = stack.at(-1);
    if (!lastTx) {
      return 'nothing';
    }
    return (
      lastTx.title ??
      uniq(lastTx.actions)
        .map((a) => capitalize(a.replace(/_/g, ' ')))
        .join(', ')
    );
  };

  const undoHint = createUndoHint(undoStack);
  const redoHint = createUndoHint(redoStack);

  const undo = useMemo(
    () => ({
      beginTransaction: beginUndoTransaction,
      endTransaction: endUndoTransaction,
      transactionWrapper,
      withTransaction,
      undo: undoFn,
      redo: redoFn,
      clearUndoStack,
      canUndo,
      canRedo,
      undoHint,
      redoHint,
    }),
    [
      beginUndoTransaction,
      endUndoTransaction,
      transactionWrapper,
      withTransaction,
      undoFn,
      redoFn,
      clearUndoStack,
      canUndo,
      canRedo,
      undoHint,
      redoHint,
    ],
  );

  const apolloMutationCompletedAt = useAtomValue(apolloMutationCompletedAtAtom);
  useEffect(() => {
    if (apolloMutationCompletedAt !== 0) {
      clearUndoStack();
    }
  }, [apolloMutationCompletedAt, clearUndoStack]);

  const copyScenarioToNew = useCallback(
    (
      s: InternalScenarioSchedule | InternalBackupSchedule | null | undefined,
      opts?: { keepName: boolean },
    ): InternalUserSchedule => {
      assertDefined(currentUserId, 'currentUserId');
      return buildCopiedScenario(s, {
        currentUserId,
        scenarios,
        initials,
        keepName: opts?.keepName,
        getDefaultScenario: buildDefaultScenario,
      });
    },
    [buildDefaultScenario, scenarios, currentUserId, initials],
  );

  const createScenario = useCallback(
    (
      scenario?: InternalScenarioSchedule,
      opts?: { keepName: boolean; selectCreated: boolean },
    ) => {
      const isDuplicated = !!scenario;
      const newScenario: InternalUserSchedule = copyScenarioToNew(
        scenario,
        opts,
      );
      dispatch({
        type: 'CREATE_SCENARIO',
        scenario: { ...newScenario, shared: false },
        isDuplicated,
      });

      if (opts?.selectCreated !== false) {
        selectScenario(newScenario.clientId);
      }
    },
    [selectScenario, copyScenarioToNew],
  );

  const createEmptyScenario = useCallback(() => {
    const newScenario: InternalUserSchedule = buildEmptyScenario();
    dispatch({
      type: 'CREATE_SCENARIO',
      scenario: newScenario,
      isDuplicated: false,
    });
    selectScenario(newScenario.clientId);
  }, [buildEmptyScenario, selectScenario]);

  const mergeScenario = useCallback(
    (
      sourceCID: ScenarioSchedule['clientId'],
      targetCID: ScenarioSchedule['clientId'],
    ) => {
      dispatch({
        type: 'MERGE_SCENARIO',
        sourceCID,
        targetCID,
      });
    },
    [],
  );

  /** Absolutely overwrite current scenarios with no regard for whatever existed before. */
  const setScenarios = useCallback(
    (
      scenarios:
        | ScenarioSchedule[]
        | ((oldScenarios: ScenarioSchedule[]) => ScenarioSchedule[]),
    ) => {
      dispatch({
        type: 'SET_SCENARIOS',
        scenarios,
      });
    },
    [],
  );

  /** Assign scenarios but preserve existing ones merge those present in both sets.
   */
  const updateScenarios = useCallback(
    (scenarios: InternalScenarioSchedule[], source?: ScenarioUpdateSource) => {
      dispatch({
        type: 'UPDATE_SCENARIOS',
        scenarios,
        source,
      });
    },
    [],
  );

  const updateScenariosOnReconnect = useCallback(
    (scenarios: (Scenarios_ScenarioFragment | null)[]) => {
      if (!archivedData?.allPortCalls?.nodes) {
        return;
      }

      const parsedArchivedData = parseArchivedData(archivedData);

      updateScenarios(
        scenarios
          .filter(isDefined)
          .map((s) => {
            const parsed = parseScenario(s);
            if (parsed) {
              return updateScenarioFromArchivedState(
                parsed,
                parsedArchivedData,
              );
            }
            return parsed;
          })
          .filter(isDefined)
          .map((s) => migrateAll(s, { persistedShipmentByClientId }))
          .filter((s) => s.organizationId === currentOrgId),
      );
    },
    [archivedData, currentOrgId, persistedShipmentByClientId, updateScenarios],
  );

  /*
   * Load saved scenarios and filter out archived items from them
   */
  useOnce(
    () => {
      const nodes = savedScenariosData?.allScenarios?.nodes;
      if (nodes && archivedData?.allPortCalls?.nodes) {
        updateScenariosOnReconnect(nodes);
        setSavedScenariosInitialized(true);
      }
    },
    !savedScenariosLoading && !archivedDataLoading && !dataLoading,
  );

  const scenariosRef = useRef(scenarios);
  scenariosRef.current = scenarios;

  const org = (currentOrgId ?? -1).toString();
  useScenarioUpdatedSubscription({
    variables: {
      org,
    },
    skip: !currentOrgId,
    fetchPolicy: 'no-cache',
    onData: async function (options) {
      const scenario = options.data.data?.scenarioUpdated?.scenario;
      if (!scenario) {
        // When scenario is unshared the policy no longer allows access to it, so we need to compare available scenarios against local ones.
        const { data } = await client.query<
          ScenariosListQueryResult['data'],
          ScenariosListQueryVariables
        >({
          query: ScenariosListDocument,
          fetchPolicy: 'no-cache',
        });
        const savedScenarios = compact(data?.allScenarios?.nodes);
        const scenariosToRemove = getSharedScenariosToRemoveLocally(
          savedScenarios,
          scenariosRef.current,
        );

        if (scenariosToRemove.length > 0) {
          toast.info(
            `Scenario(s) ${scenariosToRemove
              .map((s) => s.name ?? '(unnamed)')
              .join(', ')} were unshared`,
          );
          setScenarios(
            scenariosRef.current.filter((s) => !scenariosToRemove.includes(s)),
          );
        }
        return;
      }

      const parsed = parseScenario(scenario);
      if (!parsed) {
        console.error(`Failed to parse updated scenario ${scenario.clientId}`);
        return;
      }

      const cid = parsed.clientId;
      if (parsed.isIntegrationMaster) {
        setScenarios((oldScenarios) => {
          const scenario = oldScenarios.find((s) => s.clientId === cid);
          return scenario
            ? oldScenarios.map((s) => (s === scenario ? parsed : s))
            : [parsed, ...oldScenarios];
        });
      } else {
        updateScenarios([parsed], 'scenario');
      }

      const isDeletedByThisUser = findByClientId(
        scenariosRef.current,
        cid,
      )?.deletedAt;
      if (!isDeletedByThisUser && parsed.updatedBy !== currentUserId) {
        toast.info(
          `Scenario ${parsed.name ?? '(unnamed)'} was ` +
            (parsed.deletedAt ? 'removed' : 'updated'),
        );
      }
    },
  });

  const updateScenariosFromCommit = useCallback(
    (
      masterScenario: MasterSchedule,
      updateUserScenarios: boolean,
      updateScenarioPortCallTimes = false,
    ) => {
      dispatch({
        type: 'UPDATE_SCENARIOS_FROM_COMMIT',
        masterScenario,
        updateUserScenarios,
        updateScenarioPortCallTimes,
      });
      dispatch({
        type: 'CLEAR_UNDO_STACK',
      });
    },
    [],
  );

  const deleteScenarioCompletely = useCallback(
    async (scenarioCID: ScenarioSchedule['clientId']) => {
      const scenario = assertedDefined(
        findByClientId(scenarios, scenarioCID),
        'expected to find scenario to delete',
      );

      const { data } = await client.query<
        ScenarioByClientIdQueryResult['data'],
        ScenarioByClientIdQueryVariables
      >({
        query: ScenarioByClientIdDocument,
        variables: {
          clientId: scenario.clientId,
        },
        fetchPolicy: 'no-cache',
      });

      const existing = data?.scenario?.nodes[0];

      if (existing) {
        await client.mutate<
          DeleteScenarioMutationResult['data'],
          DeleteScenarioMutationVariables
        >({
          mutation: DeleteScenarioDocument,
          variables: {
            input: { id: existing.id },
          },
          errorPolicy: 'ignore',
          fetchPolicy: 'no-cache',
        });
      }

      dispatch({
        type: 'DELETE_SCENARIO',
        scenarioCID,
      });
    },

    [client, scenarios],
  );

  const allInitialized =
    !dataLoading && !savedScenariosLoading && localSaveLoadCompleted;

  useOnce(() => {
    const DELETE_AFTER_DAYS = 90;
    const threshold = new Date();
    threshold.setDate(threshold.getDate() - DELETE_AFTER_DAYS);
    const thresholdStr = threshold.toISOString();

    for (const scenario of scenarios) {
      if (!scenario.deletedAt) {
        continue;
      }
      if (scenario.deletedAt > thresholdStr) {
        continue;
      }
      console.info(
        `Deleting scenario ${scenario.clientId} from trash`,
        scenario,
      );
      void deleteScenarioCompletely(scenario.clientId);
    }
  }, allInitialized);

  const createAssignment = useCallback(
    (scheduleEntry: Entry, estimateIdOrScenario: ClientId | 'scenario') => {
      dispatch({
        type: 'CREATE_ASSIGNMENT',
        assignment: scheduleEntry,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const createAndUpdateAssignments = useCallback(
    (changed: ChangedEntries, estimateIdOrScenario: ClientId | 'scenario') => {
      dispatch({
        type: 'CREATE_AND_UPDATE_ASSIGNMENTS',
        changed,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const createEstimateTimeline = useCallback(
    (
      vesselId: ShipId,
      newEstimateId: ClientId | undefined,
      oldEstimateIdOrScenario: ClientId | 'scenario',
      currentVesselReservations: VesselReservationFragment[],
      currentOpenPositions: OpenPositionsFragment[],
    ) => {
      dispatch({
        type: 'CREATE_ESTIMATE_TIMELINE',
        vesselId,
        newEstimateId,
        oldEstimateIdOrScenario,
        currentVesselReservations,
        currentOpenPositions,
      });
    },
    [],
  );

  const applyEstimateTimeline = useCallback(
    async (
      estimateId: ClientId,
      keepEstimates: boolean,
      currentVesselReservations: VesselReservationFragment[],
      currentOpenPositions: OpenPositionsFragment[],
    ) => {
      await applyFreightRateOverridesToVC({
        estimateId,
        scenarios,
        orderById,
        client,
      });

      await applyEstimateReservationsAndOpenPositions({
        estimateId,
        scenarios,
        currentVesselReservations,
        currentOpenPositions,
        client,
      });

      dispatch({
        type: 'APPLY_ESTIMATE_TIMELINE',
        estimateId,
        keepEstimates,
      });
    },
    [client, orderById, scenarios],
  );

  const deleteEstimateTimeline = useCallback((estimateId: ClientId) => {
    dispatch({
      type: 'DELETE_ESTIMATE_TIMELINE',
      estimateId,
    });
  }, []);

  const addReservationToEstimate = useCallback(
    (estimateId: ClientId, vesselReservation: VesselReservationFragment) => {
      dispatch({
        type: 'ADD_RESERVATION_TO_ESTIMATE',
        estimateId,
        vesselReservation,
      });
    },
    [],
  );

  const addOpenPositionToEstimate = useCallback(
    (estimateId: ClientId, openPosition: OpenPositionsFragment) => {
      dispatch({
        type: 'ADD_OPEN_POSITION_TO_ESTIMATE',
        estimateId,
        openPosition,
      });
    },
    [],
  );

  const removeReservationFromEstimate = useCallback(
    (estimateId: ClientId, vesselReservationId: number) => {
      dispatch({
        type: 'REMOVE_RESERVATION_FROM_ESTIMATE',
        estimateId,
        vesselReservationId,
      });
    },
    [],
  );

  const removeOpenPositionFromEstimate = useCallback(
    (estimateId: ClientId, openPositionId: number) => {
      dispatch({
        type: 'REMOVE_OPEN_POSITION_FROM_ESTIMATE',
        estimateId,
        openPositionId,
      });
    },
    [],
  );

  const setEstimateFreightOverrides = useCallback(
    (
      estimateId: ClientId,
      overridesByOrderId: FreightRateOverridesByOrderId,
    ) => {
      dispatch({
        type: 'SET_ESTIMATE_FREIGHT_OVERRIDES',
        estimateId,
        overridesByOrderId,
      });
    },
    [],
  );

  const setEstimateShipmentFreightOverrides = useCallback(
    (
      estimateId: ClientId,
      overridesByShipmentId: ShipmentFreightOverridesByShipmentId,
    ) => {
      dispatch({
        type: 'SET_ESTIMATE_SHIPMENT_FREIGHT_OVERRIDES',
        estimateId,
        overridesByShipmentId,
      });
    },
    [],
  );

  const deleteAssignment = useCallback(
    (orderId: Order['id'], estimateIdOrScenario: ClientId | 'scenario') => {
      dispatch({
        type: 'DELETE_ASSIGNMENT',
        orderId,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const deleteEvent = useCallback(
    (
      eventId: ClientId,
      eventType: EventCategory.Single | EventCategory.OffHire,
      estimateIdOrScenario: ClientId | 'scenario',
    ) => {
      dispatch({
        type: 'DELETE_EVENT',
        eventId,
        eventType,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const resetAssignmentsToMaster = useCallback(
    (orderIds: OrderId[], estimateIdOrScenario: ClientId | 'scenario') => {
      dispatch({
        type: 'RESET_ASSIGNMENTS_TO_MASTER',
        orderIds,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const setOrderVoyageCharter = useCallback(
    (
      orderId: OrderId,
      voyageCharterId: VoyageCharterId | string | null,
      estimateIdOrScenario: ClientId | 'scenario',
    ) => {
      dispatch({
        type: 'SET_ORDER_VOYAGE_CHARTER',
        orderId,
        voyageCharterId,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const setVoyageCharterForOrders = useCallback(
    (orders: ScenarioOrder[], estimateIdOrScenario: ClientId | 'scenario') => {
      dispatch({
        type: 'SET_VOYAGE_CHARTER_FOR_ORDERS',
        orders,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const updateOrder = useCallback(
    (
      orderId: OrderId,
      patch: Partial<ScenarioOrderPatch['patch']>,
      estimateIdOrScenario: ClientId | 'scenario',
    ) => {
      dispatch({
        type: 'UPDATE_ORDER',
        orderId,
        patch,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const saveAllocations = useCallback(
    (
      allocations: CargoAllocation[],
      estimateIdOrScenario: ClientId | 'scenario',
    ) => {
      dispatch({
        type: 'SAVE_ALLOCATIONS',
        allocations,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const patchShipment = useCallback(
    (
      clientId: ClientId,
      patch: PatchShipment,
      estimateIdOrScenario: ClientId | 'scenario',
    ) => {
      dispatch({
        type: 'PATCH_SHIPMENT',
        clientId,
        patch,
        estimateIdOrScenario,
      });
    },
    [],
  );

  const setAutoSync = useCallback(
    (newValue: InternalScenarioSchedule['autoUpdateFromMaster']) => {
      dispatch({
        type: 'SET_SCENARIO_AUTO_SYNC',
        newValue,
      });
    },
    [],
  );

  const toggleShortlistOrders = useCallback(
    (orderIds: OrderId[], shortlisted: boolean) => {
      dispatch({
        type: 'TOGGLE_SHORTLIST_ORDERS',
        orderIds,
        shortlisted,
      });
    },
    [],
  );

  const setShortlist = useCallback((orderIds: OrderId[]) => {
    dispatch({
      type: 'SET_SHORTLIST',
      orderIds,
    });
  }, []);

  const toggleShortlistVessels = useCallback((vesselId: ShipId) => {
    dispatch({
      type: 'TOGGLE_SHORTLIST_VESSEL',
      vesselId,
    });
  }, []);

  const setShortlistVessels = useCallback((vesselIds: ShipId[]) => {
    dispatch({
      type: 'SET_SHORTLIST_VESSELS',
      vesselIds,
    });
  }, []);

  const setShipmentFreight = useCallback(
    (
      shipmentClientId: ClientId,
      freight: { freightAmount: number; freightUnit: PaymentUnit } | null,
    ) => {
      dispatch({
        type: 'SET_SHIPMENT_FREIGHT',
        shipmentClientId,
        freight,
      });
    },
    [],
  );

  const setShipmentExtraFreights = useCallback(
    (
      shipmentClientId: ClientId,
      newEntries: [
        {
          clientId: ClientId;
          name: string;
          freightAmount: number;
          freightUnit: PaymentUnit;
          description: string;
          id: number | null;
          shipmentDbId: number | null;
        },
      ],
    ) => {
      dispatch({
        type: 'SET_SHIPMENT_EXTRA_FREIGHT',
        shipmentClientId,
        newEntries,
      });
    },
    [],
  );

  const scenariosExist = scenarios.length > 0;

  // Apply new committed data, triggered by updates in committed data
  useEffect(() => {
    if (
      !dataLoading &&
      !savedScenariosLoading &&
      (schedule.length > 0 ||
        coaAssignments.length > 0 ||
        handledBySpots.length > 0) &&
      scenariosExist
    ) {
      updateScenariosFromCommit(
        buildMasterScenario(),
        opts.autoUpdateUserScenarios,
        isAutoAcceptUpdatesEnabled,
      );
    }
  }, [
    buildMasterScenario,
    coaAssignments,
    dataLoading,
    handledBySpots,
    isAutoAcceptUpdatesEnabled,
    opts.autoUpdateUserScenarios,
    masterCommitAt,
    orderById,
    portCallsById,
    savedScenariosLoading,
    scenariosExist,
    schedule,
    updateScenariosFromCommit,
  ]);

  useEffect(() => {
    async function checkLocalSave() {
      if (
        archivedDataLoading ||
        !archivedData ||
        dataLoading ||
        localSaveChecked.current
      ) {
        return;
      }
      localSaveChecked.current = true;

      const parsedArchivedData = parseArchivedData(archivedData);

      const localSave = await readStore('scenarios');
      updateScenarios(
        (localSave ?? [])
          .filter(
            (s) =>
              !isSuggestionScenario(s) &&
              !isMasterScenario(s) &&
              !isBackupScenario(s) &&
              s.isIntegrationMaster !== true,
          )
          .filter((s) => s.organizationId === currentOrgId)
          .map((scenario) =>
            updateScenarioFromArchivedState(scenario, parsedArchivedData),
          )
          .map((s) =>
            isUserScenario(s)
              ? migrateAll(s, { persistedShipmentByClientId })
              : s,
          ),
        'localsave',
      );
      setLocalSaveLoadCompleted(true);
    }

    void checkLocalSave();
  }, [
    archivedDataLoading,
    buildDefaultScenario,
    updateScenarios,
    currentOrgId,
    archivedData,
    persistedShipmentByClientId,
    dataLoading,
  ]);

  const getSharedScenariosToRemoveLocally = (
    savedScenarios: { clientId: string }[],
    localScenarios: InternalScenarioSchedule[],
  ) => {
    /** Scenarios locally marked as shared and created by someone else, but no longer available -> has been unshared by creator */
    return localScenarios.filter(
      (s) =>
        s.shared &&
        s.createdBy !== currentUserId &&
        !savedScenarios.find(
          (saved) => parseClientId(saved.clientId) === s.clientId,
        ),
    );
  };

  // Create a new user scenario if none exists
  const notify = useNotify({ toastId: 'add-scenario', dismissible: true });
  useEffect(() => {
    const noEditableScenarios = scenarios
      .filter(isUserScenario)
      .every((s) => s.deletedAt);

    if (
      !dataLoading &&
      localSaveLoadCompleted &&
      noEditableScenarios &&
      savedScenariosInitialized
    ) {
      notify(`No editable scenario present, adding...`, {
        type: toast.TYPE.WARNING,
      });
      createScenario(undefined);
    }
  }, [
    createScenario,
    dataLoading,
    localSaveLoadCompleted,
    notify,
    savedScenariosInitialized,
    scenarios,
  ]);

  /** This conditional effect runs its body when data from all sources has initialized */
  useOnce(
    () => {
      const scenariosToAdd: InternalScenarioSchedule[] = [];
      if (!scenarios.find(isMasterScenario)) {
        scenariosToAdd.push(buildMasterScenario());
      }
      const newScenarios = scenarios.concat(scenariosToAdd);
      const savedScenarios = compact(savedScenariosData?.allScenarios?.nodes);
      const scenariosToRemove = getSharedScenariosToRemoveLocally(
        savedScenarios,
        scenarios,
      );

      if (scenariosToAdd.length > 0 || scenariosToRemove.length > 0) {
        setScenarios((scenarios) => [
          ...scenariosToAdd,
          ...scenarios.filter(
            (s) => !scenariosToRemove.find((r) => r.clientId === s.clientId),
          ),
        ]);
      }

      const addedUserScenario = findLast(scenariosToAdd, (s) =>
        isUserScenario(s),
      );

      if (addedUserScenario) {
        // New user scenario was added, auto select it
        selectScenario(addedUserScenario.clientId);
      } else if (
        // Select whatever was saved if it's not already selected (another scenario could be activated by reducer if the one referred by save wasn't available earlier)
        scenarioCIDSaved !== null &&
        scenarioCIDSaved !== selectedScenarioCID &&
        findByClientId(
          newScenarios.filter((s) => !s.deletedAt),
          scenarioCIDSaved,
        )
      ) {
        selectScenario(scenarioCIDSaved);
      }
      // Check that scenario data from all sources is present
    },
    !dataLoading && savedScenariosInitialized && localSaveLoadCompleted,
  );

  // Update committed (master) scenario whenever its dependent data has changed
  useEffect(() => {
    if (!currentUserId || !currentOrgId) {
      return;
    }
    const updated = buildMasterScenario();
    setScenarios((oldScenarios) =>
      oldScenarios.map((s) => (isMasterScenario(s) ? updated : s)),
    );
  }, [buildMasterScenario, setScenarios, currentUserId, currentOrgId]);

  const updateScenariosAfterArchive = useCallback(async () => {
    const archivedData = await client.query<
      ArchivedDataQueryResult['data'],
      ArchivedDataQueryVariables
    >({
      query: ArchivedDataDocument,
      fetchPolicy: 'no-cache',
    });

    const archived = archivedData.data;
    if (!archived) {
      const msg = `Could not fetch data for archived items`;
      toast.error(msg);
      console.error(msg);
      throw new Error(msg);
    }

    const committedData = parseArchivedData(archived);

    const newScenarios = scenarios.map((scenario) =>
      updateScenarioFromArchivedState(scenario, committedData),
    );

    setScenarios(newScenarios);
  }, [client, scenarios, setScenarios]);

  const renameScenario = useCallback(
    async (clientId: ClientId, scenarioName: string) => {
      const toastId = toast.info('Renaming scenario...');

      const { data } = await client.query<
        ScenarioByClientIdQueryResult['data'],
        ScenarioByClientIdQueryVariables
      >({
        query: ScenarioByClientIdDocument,
        variables: {
          clientId,
        },
        fetchPolicy: 'no-cache',
      });

      const existing = data?.scenario?.nodes[0];

      if (existing) {
        const { errors } = await client.mutate<
          UpdateScenarioMutationResult['data'],
          UpdateScenarioMutationVariables
        >({
          mutation: UpdateScenarioDocument,
          variables: {
            input: {
              id: existing.id,
              scenarioPatch: {
                name: scenarioName,
              },
            },
          },
          fetchPolicy: 'no-cache',
        });

        if (!errors) {
          toast.update(toastId, {
            type: 'success',
            render: 'Scenario renamed',
          });
        } else {
          console.error('Failed to rename scenario:', ...(errors ?? []));
          toast.update(toastId, {
            type: 'error',
            render:
              'Failed to save new scenario name. Someone else may have removed it already.',
          });
        }
      } else {
        toast.update(toastId, {
          type: 'success',
          render: 'Scenario renamed',
        });
      }

      setScenarios(
        scenarios.map((s) =>
          s.clientId === clientId ? { ...s, name: scenarioName } : s,
        ),
      );
    },
    [client, scenarios, setScenarios],
  );

  const saveScenario = useCallback(
    async (
      scenarioCID: ScenarioSchedule['clientId'],
      opts: {
        share?: boolean;
        data?: UserSchedule;
        updateOnly?: boolean;
        completedToast?: string;
        toastId?: ToastNotificationId;
      },
    ) => {
      const saveScenarioToastId: ToastNotificationId =
        opts.toastId ?? 'saveScenarioToastId';
      const scenario = cloneDeep(
        opts.data ?? scenarios.find((s) => s.clientId === scenarioCID),
      );
      if (!scenario) {
        throw new Error(`No scenario selected`);
      }

      if (isSuggestionScenario(scenario)) {
        toast.error(`Cannot save suggestion scenarios`);
        return;
      }

      if (isMasterScenario(scenario)) {
        toast.error(
          `Cannot save the main scenario. Create a new user scenario from it first.`,
        );
        return;
      }

      if (isBackupScenario(scenario)) {
        toast.error(
          `Cannot save backup scenarios. Create a new user scenario from it first.`,
        );
        return;
      }

      if (isInitialScenario(scenario)) {
        toast.error(
          `Cannot save initial scenarios. Create a new user scenario first.`,
        );
        return;
      }

      assertDefined(currentUserId, 'currentUserId');
      scenario.updatedBy = currentUserId;

      if (opts.share !== undefined) {
        scenario.shared = opts.share;
      }

      delete scenario.changedAt;

      setScenarios((scenarios) =>
        scenarios.map((s) => (s.clientId === scenarioCID ? scenario : s)),
      );

      if (opts.updateOnly && !scenario.id) {
        return scenario;
      }

      toast.info('Saving scenario...', {
        toastId: saveScenarioToastId,

        isLoading: true,
      });

      const { data } = await client.query<
        ScenarioByClientIdQueryResult['data'],
        ScenarioByClientIdQueryVariables
      >({
        query: ScenarioByClientIdDocument,
        variables: {
          clientId: scenario.clientId,
        },
        fetchPolicy: 'no-cache',
      });

      const existing = data?.scenario?.nodes[0];

      const input = createScenarioInput(scenario);

      if (existing) {
        const { errors } = await client.mutate<
          UpdateScenarioMutationResult['data'],
          UpdateScenarioMutationVariables
        >({
          mutation: UpdateScenarioDocument,
          variables: {
            input: {
              id: existing.id,
              scenarioPatch: {
                ...input,
              },
            },
          },
          fetchPolicy: 'no-cache',
        });

        if (!errors) {
          toast.update(saveScenarioToastId, {
            type: 'success',
            render: opts.completedToast ?? 'Scenario saved',
            isLoading: false,
            autoClose: 2000,
          });
        } else {
          console.error('Failed to save scenario:', ...(errors ?? []));
          toast.update(saveScenarioToastId, {
            type: 'error',
            render:
              'Failed to save updated scenario. Someone else may have removed it already.',
            isLoading: false,
            autoClose: 2000,
          });
        }
      } else if (!opts.updateOnly) {
        const { data, errors } = await client.mutate<
          CreateScenarioMutationResult['data'],
          CreateScenarioMutationVariables
        >({
          mutation: CreateScenarioDocument,
          variables: {
            input: {
              scenario: exactProps<ScenarioInput, typeof input>(input),
            },
          },
          fetchPolicy: 'no-cache',
        });

        if (!errors && data?.createScenario?.scenario?.id !== undefined) {
          const id = data.createScenario.scenario.id;
          setScenarios((scenarios) =>
            scenarios.map((s) =>
              s.clientId === scenarioCID ? { ...s, id } : s,
            ),
          );
          toast.update(saveScenarioToastId, {
            type: 'success',
            render: opts.completedToast ?? 'Scenario saved',
            isLoading: false,
            autoClose: 2000,
          });
        } else {
          console.error('Failed to save scenario:', ...(errors ?? []));
          toast.update(saveScenarioToastId, {
            type: 'error',
            render: 'Failed to save new scenario.',
            isLoading: false,
            autoClose: 2000,
          });
        }
      }
      return scenario;
    },
    [client, currentUserId, scenarios, setScenarios],
  );

  const deleteScenarioToTrash = useCallback(
    async (scenarioCID: ScenarioSchedule['clientId']) => {
      const scenario = assertedDefined(
        findByClientId(scenarios, scenarioCID),
        'expected to find scenario to delete',
      );
      if (scenario.type === 'master') {
        toast.error("Can't remove master scenario!");
        return;
      }
      if (scenario.type === 'suggestion') {
        setScenarios((prev) => prev.filter((s) => s.clientId !== scenarioCID));
        toast.success(`Scenario ${scenario.name} removed`);
        return;
      }
      if (scenario.type === 'initial') {
        toast.error("Can't remove initial scenario!");
        return;
      }
      if (scenario.type === 'backup') {
        await deleteScenarioCompletely(scenarioCID);
        return;
      }

      const newScenarioData = {
        ...scenario,
        deletedAt: new Date().toISOString(),
      };

      if (scenario.id && scenario.createdBy === currentUserId) {
        await saveScenario(scenarioCID, {
          data: newScenarioData,
          updateOnly: true,
          completedToast: `Scenario ${scenario.name} deleted`,
        });
      } else {
        clearUndoStack();
        setUserLocallyDeletedScenarios((prevDeleted) =>
          uniq([...prevDeleted, scenarioCID]),
        );
      }

      setScenarios(
        scenarios.map((s) =>
          s.clientId === scenarioCID ? newScenarioData : s,
        ),
      );

      toast(`Scenario ${scenario.name} moved to trash`, {
        type: toast.TYPE.INFO,
      });
    },
    [
      scenarios,
      currentUserId,
      setScenarios,
      deleteScenarioCompletely,
      saveScenario,
      clearUndoStack,
      setUserLocallyDeletedScenarios,
    ],
  );

  const restoreScenario = useCallback(
    async (scenarioCID: ScenarioSchedule['clientId']) => {
      const scenario = assertedDefined(
        findByClientId(scenarios, scenarioCID),
        'expected to find scenario to delete',
      );
      if (scenario.type === 'master') {
        toast.error('There is no point of restoring master scenario!');
        return;
      }
      if (scenario.type === 'suggestion') {
        toast.error('There is no point of restoring suggestion scenario!');
        return;
      }
      if (scenario.type === 'initial') {
        toast.error('There is no point of restoring initial scenario!');
        return;
      }
      if (scenario.type === 'backup') {
        toast.error('Backup scenario cannot be restored');
        return;
      }

      const newScenarioData = {
        ...scenario,
        deletedAt: null,
      };
      if (scenario.id) {
        await saveScenario(scenarioCID, {
          data: newScenarioData,
          updateOnly: true,
        });
      }

      setUserLocallyDeletedScenarios((prevDeleted) =>
        prevDeleted.filter((cid) => cid !== scenarioCID),
      );
      setScenarios(
        scenarios.map((s) =>
          s.clientId === scenarioCID ? newScenarioData : s,
        ),
      );
    },
    [scenarios, setUserLocallyDeletedScenarios, setScenarios, saveScenario],
  );

  const restoreIntegrationScenario = useCallback(async () => {
    const integrationScenario = scenarios.find(
      (scenario) => scenario.isIntegrationMaster,
    );
    if (!integrationScenario) {
      return;
    }
    const scenarioCID = integrationScenario.clientId;
    const isDeleted = integrationScenario.deletedAt;
    if (isDeleted && integrationScenario.type === 'user') {
      const newScenarioData = {
        ...integrationScenario,
        deletedAt: null,
      };
      await saveScenario(scenarioCID, {
        data: newScenarioData,
        updateOnly: true,
      });

      setScenarios(
        scenarios.map((s) =>
          s.clientId === scenarioCID ? newScenarioData : s,
        ),
      );
    }

    setUserLocallyDeletedScenarios((prevDeleted) =>
      prevDeleted.filter((cid) => cid !== scenarioCID),
    );
  }, [saveScenario, scenarios, setScenarios, setUserLocallyDeletedScenarios]);

  const commitScenario: CommitScenarioFn = useEvent(
    async (scenarioCID, timelines: EventTimeline[], getScenarioData, opts) => {
      console.time('pre-commit');
      const commitScenarioToastId: ToastNotificationId =
        'commitScenarioToastId';
      // We set this flag so that effects can ignore some updates caused by Apollo cache
      // updating after mutations done in commit logic
      isCommitRunning.current = true;
      setIsCommitRunning(true);

      try {
        const previousMaster = scenarios.find(isInternalMasterScenario);

        const originalScenario = cloneDeep(
          findByClientId(scenarios.filter(isInternalUserScenario), scenarioCID),
        ) as InternalScenarioSchedule | undefined;

        if (!originalScenario) {
          toast(`Cannot save to main, no scenario selected`, {
            toastId: commitScenarioToastId,
            type: toast.TYPE.ERROR,
            isLoading: false,
          });
          return;
        }

        if (isInternalMasterScenario(originalScenario)) {
          toast(
            `Cannot save the main scenario: it already represents the main state`,
            {
              toastId: commitScenarioToastId,
              type: toast.TYPE.ERROR,
            },
          );
          return;
        }

        if (isInternalInitialScenario(originalScenario)) {
          toast(
            `Cannot save the main scenario: it already represents the main state`,
            {
              toastId: commitScenarioToastId,
              type: toast.TYPE.ERROR,
            },
          );
          return;
        }

        if (isSuggestionScenario(originalScenario)) {
          toast(
            `Cannot save suggestion scenarios to main. Create a new scenario from it first.`,
            {
              toastId: commitScenarioToastId,
              type: toast.TYPE.ERROR,
            },
          );
          return;
        }

        if (isInternalBackupScenario(originalScenario)) {
          toast(
            `Cannot save backup scenarios to main. Create a new scenario from it first.`,
            {
              toastId: commitScenarioToastId,
              type: toast.TYPE.ERROR,
            },
          );
          return;
        }

        const masterScenario = scenarios.find(isInternalMasterScenario);

        if (!masterScenario) {
          toast(`Cannot find master scenario, try to refresh the page.`, {
            toastId: commitScenarioToastId,
            type: toast.TYPE.ERROR,
          });
          return;
        }

        // Ensure timings in scenario are the same as on the timelines, also may add some other computed values to scenario e.g. port calls.
        const scenario = updateScenarioFromTimelines(
          originalScenario,
          timelines,
        );

        let scenarioDataToCommit = scenario;
        let timelinesToCommit = timelines;

        // Sync user scenario with master first if the commit is the merge type
        // Note, we don't want to save this synced version to store (this would automatically bring all master assignments into current scenario, which may not be desired)
        if (
          opts.policy === ScenarioCommitPolicy.MERGE_COMPATIBLE &&
          previousMaster
        ) {
          scenarioDataToCommit = getMergedScenario(scenario, previousMaster, {
            sourceIsCommit: true,
            directives: opts.mergeDirectives,
          });
          timelinesToCommit = getScenarioData(scenarioDataToCommit).timelines;
        }

        assertDefined(currentUser, 'currentUser');
        const commitResult = await doCommitScenario(
          client,
          scenarioDataToCommit,
          masterScenario ?? null,
          timelinesToCommit,
          voyageNamingRuleByShipId,
          {
            ...opts,
            currentUser,
            getScenarioData,
          },
        );

        if (!commitResult) {
          return;
        }

        const commitTimestamp = dateStringToNumOpt(
          commitResult.commitResult.data?.commit?.committedAt,
        );

        /** Some id, state and relation updates to original scenario. We do not modify the original scenario in store in any other ways. */
        const newScenario = updateScenarioFromCommitResult(
          scenario,
          commitResult.commitInput,
          commitResult.commitResult.data,
          scenarioDataToCommit,
        );

        // Counter intuitively, we are in sync when we override. If we make merge commit the merge is only present during scenario, but it's not applied to the resulting local scenario.
        newScenario.syncedWithMasterAt =
          opts.policy === ScenarioCommitPolicy.OVERRIDE
            ? (commitTimestamp ?? Date.now())
            : scenario.syncedWithMasterAt;

        newScenario.updatedBy = assertedDefined(currentUserId, 'currentUserId');

        delete newScenario.changedAt;

        let newScenarios = scenarios.map((s) =>
          s.clientId === scenarioCID ? newScenario : s,
        );

        const KEEP_BACKUP_SCENARIOS = 5;
        const oldBackups: InternalBackupSchedule[] = newScenarios.filter(
          isInternalBackupScenario,
        );
        const keptBackups = sortBy(oldBackups, (s) => -s.backedUpAt).slice(
          0,
          KEEP_BACKUP_SCENARIOS - 1,
        );

        // Drop extra backups
        newScenarios = (
          newScenarios.filter(
            (s) => !isInternalBackupScenario(s),
          ) as InternalScenarioSchedule[]
        ).concat(keptBackups);

        // Backup previous committed (master) scenario into a new backup scenario
        const newBackup: InternalBackupSchedule = {
          ...copyScenarioToNew(previousMaster),
          type: 'backup',
          isReadOnly: true,
          backedUpAt: Date.now(),
          name: `COMMITTED ${moment().format('yyyy-MM-DD')}`,
          createdAt: new Date().toISOString(),
        };
        newScenarios.push(newBackup);

        opts.statusUpdate?.({ currentActionName: null });

        dispatch({
          type: 'SET_SCENARIOS',
          scenarios: newScenarios,
        });
        dispatch({
          type: 'SELECT_SCENARIO',
          scenarioCID,
        });

        void writeStore(
          'scenarios',
          newScenarios.filter((s) => s.isIntegrationMaster !== true),
        );

        toast('Scenario saved to main successfully', {
          toastId: commitScenarioToastId,
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 2000,
        });

        // Update the scenario in DB if it's already saved, otherwise no need to save it here. REF: https://seaber2.myjetbrains.com/youtrack/issue/SEABER-2881/SE-2-planners-reverting-each-others-assignments
        void saveScenario(newScenario.clientId, {
          data: newScenario,
          updateOnly: true,
          toastId: commitScenarioToastId,
        });
      } catch (err) {
        // Save original scenario on error for later debugging
        if (scenario.shared) {
          try {
            void saveScenario(scenario.clientId, {
              toastId: commitScenarioToastId,
            });
          } catch (err) {
            console.error('Error saving scenario after failed commit', err);
          }
        }

        // Throw error again for commit caller to handle it
        throw err;
      } finally {
        isCommitRunning.current = false;
        setIsCommitRunning(false);
        onFinishCommit();
        console.timeEnd('commit');

        if (
          typeof window !== 'undefined' &&
          process.env.NODE_ENV === 'development'
        ) {
          import('../lib/memoryMonitor').then(({ logMemorySnapshot }) => {
            logMemorySnapshot('Scenario commit');
          });
        }
      }
    },
  );

  const updateAssignmentsSharing = useCallback(
    async (ids: Set<OrderId>, shared: boolean) => {
      const data = await client.mutate<
        PublishOrdersMutation,
        PublishOrdersMutationVariables
      >({
        mutation: PublishOrdersDocument,
        variables: {
          ids: Array.from(ids),
          shared: shared,
        },
      });
      if (data.errors?.length) {
        throw new Error(JSON.stringify(data.errors));
      }
      const po = data.data?.publishOrders;
      if (!po) {
        throw new Error('No results');
      }
      const cIds = new Set<ClientId>();
      for (const assignments of [po.sos, po.hbcs, po.hbss]) {
        for (const cid of assignments ?? []) {
          cIds.add(parseClientId(cid));
        }
      }
      dispatch({
        type: 'UPDATE_ASSIGNMENTS_SHARING',
        ids: cIds,
        shared: shared,
      });
    },
    [client],
  );

  const masterScenario = useMemo(() => {
    const masterScenario = scenarios.find(isInternalMasterScenario);
    return masterScenario ?? dummyScenario;
  }, [scenarios]);

  const masterScenarioOrdersAndShipments = useMemo(
    () =>
      getScenarioOrdersAndShipments(
        orderById,
        persistedShipmentByClientId,
        masterScenario,
        { ignoreIntegrationCargoMargins },
      ),
    [
      ignoreIntegrationCargoMargins,
      orderById,
      persistedShipmentByClientId,
      masterScenario,
    ],
  );

  const withoutLocalScenarios = useMemo((): UseScenariosResult => {
    const { scenarioOrderById, scenarioOrders, scenarioShipmentByClientId } =
      masterScenarioOrdersAndShipments;
    return {
      loading: dataLoading,
      scenarios: [masterScenario],
      selectedScenarioCID: null,
      createAssignment: () => null,
      selectScenario: () => null,
      saveScenario: async () => undefined,
      renameScenario: () => null,
      updateScenarios: () => null,
      updateScenariosOnReconnect: () => null,
      scenarioShipmentByClientId,
      scenarioOrderById,
      scenarioOrders,
      undo,
      setScenarios: () => null,
      selectedScenario: masterScenario,
      createAndUpdateAssignments: () => null,
      createEstimateTimeline: () => null,
      applyEstimateTimeline: () => null,
      deleteEstimateTimeline: () => null,
      addReservationToEstimate: () => null,
      addOpenPositionToEstimate: () => null,
      removeReservationFromEstimate: () => null,
      removeOpenPositionFromEstimate: () => null,
      deleteScenarioToTrash: () => null,
      restoreIntegrationScenario: () => null,
      deleteScenarioCompletely: () => null,
      restoreScenario: () => null,
      mergeScenario: () => null,
      deleteAssignment: () => null,
      deleteEvent: () => null,
      createScenario: () => null,
      createEmptyScenario: () => null,
      commitScenario: async () => {},
      updateAssignmentsSharing: async () => {
        // noop
      },
      resetAssignmentsToMaster: () => null,
      setOrderVoyageCharter: () => null,
      setVoyageCharterForOrders: () => null,
      updateOrder: () => null,
      updateScenariosAfterArchive: () => Promise.resolve(),
      saveAllocations: () => null,
      patchShipment: () => null,
      setAutoSync: () => null,
      toggleShortlistOrders: () => null,
      setShortlist: () => null,
      toggleShortlistVessels: () => null,
      setShortlistVessels: () => null,
      setEstimateFreightOverrides: () => null,
      setEstimateShipmentFreightOverrides: () => null,
      setShipmentFreight: () => null,
      setShipmentExtraFreights: () => null,
    };
  }, [masterScenarioOrdersAndShipments, dataLoading, masterScenario, undo]);

  if (skipLocalScenarios) {
    return withoutLocalScenarios;
  }

  return {
    loading: dataLoading || !allInitialized,
    selectedScenario: scenario,
    scenarioShipmentByClientId,
    scenarioOrderById,
    scenarioOrders,
    selectedScenarioCID,
    undo,
    scenarios,
    selectScenario,
    createScenario,
    createEmptyScenario,
    setScenarios,
    updateScenarios,
    updateScenariosOnReconnect,
    renameScenario,
    deleteScenarioToTrash,
    restoreIntegrationScenario,
    deleteScenarioCompletely,
    restoreScenario,
    mergeScenario,
    createAssignment,
    deleteAssignment,
    deleteEvent,
    commitScenario,
    saveScenario,
    createAndUpdateAssignments,
    createEstimateTimeline,
    applyEstimateTimeline,
    deleteEstimateTimeline,
    addReservationToEstimate,
    addOpenPositionToEstimate,
    removeReservationFromEstimate,
    removeOpenPositionFromEstimate,
    updateAssignmentsSharing,
    resetAssignmentsToMaster,
    setOrderVoyageCharter,
    setVoyageCharterForOrders,
    updateOrder,
    updateScenariosAfterArchive,
    saveAllocations,
    patchShipment,
    setAutoSync,
    toggleShortlistOrders,
    setShortlist,
    toggleShortlistVessels,
    setShortlistVessels,
    setEstimateFreightOverrides,
    setEstimateShipmentFreightOverrides,
    setShipmentFreight,
    setShipmentExtraFreights,
  };
};

export function suggestionScenarioToUserScenario(
  scenario: InternalSuggestionSchedule,
): InternalUserSchedule;
export function suggestionScenarioToUserScenario(
  scenario: SuggestionSchedule,
): UserSchedule;
export function suggestionScenarioToUserScenario(
  scenario: InternalSuggestionSchedule,
): InternalUserSchedule {
  const { baseScenarioId, ...rest } = scenario;
  return {
    ...rest,
    type: 'user',
    isReadOnly: false,
    clientId: generateClientId(),
  };
}
